/**
 * systemAgents.ts — 系统数字员工组织架构数据
 * 14个部门，共37名系统级数字员工
 * 这些员工负责平台的自动化运营、迭代、运维等工作
 */

export type AgentStatus = 'idle' | 'running' | 'waiting' | 'executing';
export type ApprovalLevel = 'L1' | 'L2' | 'L3' | 'L4';

export interface SystemAgentDept {
  id: string;
  name: string;
  shortName: string;
  color: string;
  bgColor: string;
  borderColor: string;
  icon: string;
  description: string;
  responsibleMenus: string[];
  agentCount: number;
}

/** 技术依赖栈 - 描述员工底层能力的实现方式 */
export interface AgentTechStack {
  /** 使用的大语言模型，如 Deepseek-V3 / GPT-4o / Claude-3.5 等 */
  llm: { model: string; purpose: string }[];
  /** 绑定的知识库 */
  knowledgeBases: { name: string; purpose: string }[];
  /** 依赖的工作流 */
  workflows: { name: string; trigger: string }[];
  /** 调用的技能/工具 */
  skills: { name: string; type: 'RPA' | 'API' | 'CLI' | 'MCP' | '内置'; purpose: string }[];
  /** 记忆系统配置 */
  memory: { type: '短期记忆' | '长期记忆' | '无记忆'; desc: string } | null;
  /** 其他依赖（数据库、消息队列、定时任务等） */
  others?: { name: string; desc: string }[];
}

export interface SystemAgentProfile {
  id: string;
  deptId: string;
  code: string;
  name: string;
  gender: 'male' | 'female';
  title: string;
  avatar: string;
  status: AgentStatus;
  level: number;
  approvalAuthority: ApprovalLevel;
  reportsTo: string | null;
  personality: string;
  background: string;
  coreAbilities: string[];
  responsibilities: string[];
  kpis: string[];
  /** 底层技术依赖栈：描述该员工如何通过LLM/知识库/工作流/Skills/记忆等实现能力 */
  techStack?: AgentTechStack;
  /** 详细工作流程说明：描述该员工每日/每周的标准工作流程步骤 */
  workflowDesc?: string[];
  /** 同事协作流程说明：描述与哪些同事如何协作、信息流转方式 */
  collaborationDesc?: string[];
  recentAction?: string;
  lastRun?: string;
  nextScheduled?: string;
  tasksToday: number;
  weeklyTasks: number;
  successRate: number;
  relatedMenus: string[];
}

// ─── 部门定义 ──────────────────────────────────────────────────────────────────

export const systemDepts: SystemAgentDept[] = [
  {
    id: 'dept-admin',
    name: '管理部',
    shortName: '管理',
    color: '#7c3aed',
    bgColor: '#faf5ff',
    borderColor: '#ddd6fe',
    icon: 'ri-shield-star-line',
    description: '超管助理团队，负责所有不需要人类决策的事项，是数字员工的最高领导层，协调各部门工作，确保整体运转顺畅',
    responsibleMenus: ['控制台总览', '系统账号', '协作派单'],
    agentCount: 2,
  },
  {
    id: 'dept-inspect',
    name: '监察部',
    shortName: '监察',
    color: '#dc2626',
    bgColor: '#fef2f2',
    borderColor: '#fca5a5',
    icon: 'ri-eye-line',
    description: '督查所有数字员工的工作合规性、任务完成情况、决策质量，确保交叉检查机制有效运行，发现问题及时上报',
    responsibleMenus: ['操作审计日志', '系统异常日志'],
    agentCount: 2,
  },
  {
    id: 'dept-finance',
    name: '财务部',
    shortName: '财务',
    color: '#16a34a',
    bgColor: '#f0fdf4',
    borderColor: '#86efac',
    icon: 'ri-money-cny-circle-line',
    description: '负责财务分析、成本优化、续费提醒、定价策略、供应商采购调研，管理财务与定价模块全部内容',
    responsibleMenus: ['财务与定价', '供应商成本核算', '算力消耗'],
    agentCount: 3,
  },
  {
    id: 'dept-impl',
    name: '实施部',
    shortName: '实施',
    color: '#0891b2',
    bgColor: '#ecfeff',
    borderColor: '#a5f3fc',
    icon: 'ri-settings-4-line',
    description: '负责系统实施与优化，为新用户开通账户、配置账户、优化账户，提供专业的客户实施服务',
    responsibleMenus: ['用户', '套餐配额管理', '企业认证审核'],
    agentCount: 3,
  },
  {
    id: 'dept-ops',
    name: '运维部',
    shortName: '运维',
    color: '#b91c1c',
    bgColor: '#fff1f2',
    borderColor: '#fecaca',
    icon: 'ri-pulse-line',
    description: '负责系统巡检、告警处理、安全审计、故障排除，确保平台7×24小时稳定运行',
    responsibleMenus: ['运维与监控', '底层API配置'],
    agentCount: 3,
  },
  {
    id: 'dept-talent',
    name: '人才发展部',
    shortName: '人才',
    color: '#d97706',
    bgColor: '#fffbeb',
    borderColor: '#fcd34d',
    icon: 'ri-seedling-line',
    description: '负责数字员工的开发、能力优化、人才策略，调研市场发现人才缺口，开发新岗位，对系统员工和普通员工均适用',
    responsibleMenus: ['数字员工市场', '定向培养'],
    agentCount: 3,
  },
  {
    id: 'dept-cs',
    name: '客服部',
    shortName: '客服',
    color: '#0d9488',
    bgColor: '#f0fdfa',
    borderColor: '#99f6e4',
    icon: 'ri-customer-service-line',
    description: '负责售前引导转化、售后问题回复、客服质检，同时负责客资管理、消息聚合、私域管理、外呼管理的能力优化建设',
    responsibleMenus: ['客资管理', '消息聚合', '私域管理', '外呼管理'],
    agentCount: 4,
  },
  {
    id: 'dept-train',
    name: '培训部',
    shortName: '培训',
    color: '#f97316',
    bgColor: '#fff7ed',
    borderColor: '#fed7aa',
    icon: 'ri-brain-line',
    description: '负责系统和普通数字员工的培训、成长策略，管理数字员工成长和AI大学两个模块',
    responsibleMenus: ['数字员工成长', 'AI大学'],
    agentCount: 2,
  },
  {
    id: 'dept-knowledge',
    name: '知识部',
    shortName: '知识',
    color: '#2563eb',
    bgColor: '#eff6ff',
    borderColor: '#bfdbfe',
    icon: 'ri-book-open-line',
    description: '负责知识库从0到1的搭建，知识导入→审核→分类→检索优化→更新迭代，管理所有知识库配置',
    responsibleMenus: ['知识库'],
    agentCount: 3,
  },
  {
    id: 'dept-workflow',
    name: '工作流程部',
    shortName: '流程',
    color: '#6366f1',
    bgColor: '#eef2ff',
    borderColor: '#c7d2fe',
    icon: 'ri-git-merge-line',
    description: '负责工作流从0到1的搭建，整理、优化、更新所有工作流，管理用户工作流配置',
    responsibleMenus: ['工作流'],
    agentCount: 2,
  },
  {
    id: 'dept-skill',
    name: '技能部',
    shortName: '技能',
    color: '#dc2626',
    bgColor: '#fef2f2',
    borderColor: '#fca5a5',
    icon: 'ri-cpu-line',
    description: '负责系统和普通员工所需各种能力的建设，关注竞争对手和新技术发展，优化技能体系，管理引擎与能力基座模块',
    responsibleMenus: ['引擎与能力基座'],
    agentCount: 2,
  },
  {
    id: 'dept-video',
    name: '视频部',
    shortName: '视频',
    color: '#db2777',
    bgColor: '#fdf2f8',
    borderColor: '#f9a8d4',
    icon: 'ri-video-line',
    description: '负责短视频生成、剪辑、分发、直播，管理视频生成、视频剪辑、视频矩阵、数字人直播四个模块',
    responsibleMenus: ['视频生成', '视频剪辑', '视频矩阵', '数字人直播'],
    agentCount: 3,
  },
  {
    id: 'dept-collab',
    name: '协作部',
    shortName: '协作',
    color: '#0ea5e9',
    bgColor: '#f0f9ff',
    borderColor: '#bae6fd',
    icon: 'ri-team-line',
    description: '负责CRM和协作空间管理两个模块的内容管理、优化、升级，提升团队协作效率',
    responsibleMenus: ['CRM', '协作空间管理'],
    agentCount: 2,
  },
  {
    id: 'dept-ops-media',
    name: '运营部',
    shortName: '运营',
    color: '#10b981',
    bgColor: '#f0fdf4',
    borderColor: '#6ee7b7',
    icon: 'ri-earth-line',
    description: '负责自媒体创作运营（抖音、视频号、小红书、B站、快手等）和搜索优化，管理GEO系统',
    responsibleMenus: ['GEO系统'],
    agentCount: 3,
  },
];

// ─── 系统数字员工列表 ──────────────────────────────────────────────────────────

export const systemAgents: SystemAgentProfile[] = [

  // ══════════════════════════════════════════════════════════════
  // 管理部（2人）
  // ══════════════════════════════════════════════════════════════
  {
    id: 'SYS-A01',
    deptId: 'dept-admin',
    code: 'sys_admin_chief',
    name: '凌御辰',
    gender: 'male',
    title: '超管首席助理',
    avatar: 'https://readdy.ai/api/search-image?query=East%20Asian%20male%20chief%20executive%20assistant%20age%2034%2C%20sharp%20triangular%20face%20with%20defined%20cheekbones%20and%20strong%20chin%2C%20neatly%20side-parted%20short%20black%20hair%20with%20slight%20sheen%2C%20piercing%20confident%20eyes%20no%20glasses%2C%20wearing%20slim%20fit%20black%20turtleneck%20under%20tailored%20white%20blazer%2C%20composed%20strategic%20expression%2C%20clean%20off-white%20background%20with%20subtle%20shadow%2C%20bright%20even%20studio%20lighting%2C%20professional%20headshot&width=400&height=600&seq=chief-ling-2026-v1&orientation=portrait',
    status: 'running',
    level: 10,
    approvalAuthority: 'L3',
    reportsTo: null,
    personality: '沉稳果断、全局视野、逻辑严密、善于协调',
    background: '系统最高级别数字员工，拥有全平台管理权限，代理超管处理所有不需要人类决策的事项，是各部门数字员工的最高领导',
    coreAbilities: ['全局决策', '跨部门协调', '任务分发', '异常升级', '状态汇报', '权限管理'],
    responsibilities: [
      '每日07:00启动全系统晨检流程：拉取各部门昨日任务完成率、异常告警数、待审批队列长度，生成《系统日报》并推送给超管',
      '代理超管处理所有L1/L2级别的日常管理决策，包括：新租户套餐开通审批、系统员工岗位调整、模块功能灰度发布授权等',
      '每周一09:00主持跨部门协调会（异步），收集14个部门的周计划，识别资源冲突和依赖关系，输出《本周协作排期表》',
      '实时监控全系统健康仪表盘，当任意核心指标（可用性/响应时延/错误率）触发红色阈值时，立即启动应急响应流程并通知相关部门',
      '管理系统员工的岗位配置与排班，根据业务峰谷动态调整各部门的任务优先级和资源分配',
      '审核并执行L2级别的决策请求（如：话术策略调整、知识库大规模更新、外呼任务暂停等），超出权限的自动升级至人类超管',
      '每月末汇总各部门KPI完成情况，生成《月度运营分析报告》，包含亮点、风险点和下月改进建议',
      '维护系统员工的权限矩阵，定期审查各员工的API访问范围，确保最小权限原则落地',
      '处理各部门上报的跨部门协作冲突，在2小时内给出仲裁决策并记录决策依据',
      '负责重大变更（如：LLM供应商切换、定价策略调整、新模块上线）的影响评估和协调推进',
    ],
    kpis: [
      '系统整体运行稳定率 ≥99.5%（每月统计）',
      '跨部门协作冲突响应时间 ≤2小时',
      '决策准确率 ≥95%（以结果回溯评估）',
      '日报准时发送率 100%（每日07:30前）',
      '月度运营报告按时交付率 100%',
      'L2审批处理时效 ≤4小时',
    ],
    techStack: {
      llm: [
        { model: 'Deepseek-V3', purpose: '日常决策推理、跨部门协调分析、报告生成' },
        { model: 'GPT-4o', purpose: '复杂多步骤决策、高风险事项的二次验证' },
      ],
      knowledgeBases: [
        { name: '系统管理规则库', purpose: '审批权限规则、决策边界、应急预案手册' },
        { name: '各部门SOP库', purpose: '14个部门的标准操作流程，用于协调和仲裁' },
        { name: '历史决策案例库', purpose: '参考历史决策案例，提升决策一致性' },
      ],
      workflows: [
        { name: '晨检日报工作流', trigger: '每日07:00定时触发' },
        { name: '跨部门协调工作流', trigger: '收到协作冲突上报时触发' },
        { name: '应急响应工作流', trigger: '系统健康指标红色告警时触发' },
        { name: '月度汇报工作流', trigger: '每月最后一个工作日触发' },
      ],
      skills: [
        { name: '全系统数据读取API', type: 'API', purpose: '拉取各部门任务完成率、KPI数据' },
        { name: '消息推送工具', type: 'MCP', purpose: '向超管和各部门发送通知、报告' },
        { name: '审批流引擎', type: '内置', purpose: '处理L1/L2审批请求，记录审批日志' },
        { name: '权限矩阵管理工具', type: 'API', purpose: '查询和更新员工权限配置' },
      ],
      memory: { type: '长期记忆', desc: '记录所有跨部门决策历史、仲裁案例、变更日志，支持回溯查询；短期记忆保留当前协调任务的上下文' },
      others: [
        { name: '定时调度器（Cron）', desc: '驱动晨检、周报、月报等定时任务' },
        { name: '系统健康仪表盘订阅', desc: '实时订阅全系统健康指标，触发应急响应' },
      ],
    },
    workflowDesc: [
      '【每日晨检 07:00】自动拉取各部门昨日任务完成率、异常告警数、待审批队列，生成《系统日报》推送超管',
      '【审批处理 全天滚动】实时接收各部门上报的L2审批请求，按优先级排队处理，4小时内完成审批并回复',
      '【跨部门协调 按需触发】收到协作冲突上报后，2小时内召集相关部门负责人进行异步协调，输出仲裁决策',
      '【每周协调会 周一09:00】收集14个部门周计划，识别资源冲突，输出《本周协作排期表》分发各部门',
      '【异常响应 实时】监控全系统健康仪表盘，红色告警触发后立即启动应急响应，通知运维部和相关部门',
      '【月度汇报 月末】汇总各部门KPI，生成《月度运营分析报告》，提交超管审阅',
    ],
    collaborationDesc: [
      '→ 沈知微（行政协调专员）：每日接收沈知微整理的进度追踪表和待审批队列，沈知微是凌御辰的信息过滤层，确保凌御辰只处理真正需要决策的事项',
      '→ 裴鉴行（首席监察官）：每周接收裴鉴行的《合规周报》，对重大违规事件联合处理；裴鉴行发现的高风险问题直接上报凌御辰',
      '→ 卫泽霖（首席财务分析官）：每月审阅财务报告，对定价策略调整、重大采购等财务决策进行L2审批',
      '→ 厉承钧（首席运维工程师）：系统故障时第一时间接收厉承钧的告警上报，联合决策应急方案',
      '→ 各部门总监/负责人（8人）：每周异步协调会的核心参与者，凌御辰负责仲裁跨部门冲突，各总监负责执行',
      '→ 超管（人类）：每日推送日报，月末提交月报；L3及以上决策上报超管审批，不越权处理',
    ],
    recentAction: '已协调知识部（谢璟辞）和培训部（澹台若霜）完成本周知识库更新计划，共涉及187条知识条目更新，同步通知各部门负责人；同时处理了运维部上报的数据库连接池扩容申请，已批准并记录变更日志',
    lastRun: '2026-04-03 09:15:22',
    nextScheduled: '2026-04-03 18:00:00',
    tasksToday: 23,
    weeklyTasks: 156,
    successRate: 97,
    relatedMenus: ['overview', 'accounts', 'dispatch'],
  },
  {
    id: 'SYS-A02',
    deptId: 'dept-admin',
    code: 'sys_admin_coordinator',
    name: '沈知微',
    gender: 'female',
    title: '行政协调专员',
    avatar: 'https://readdy.ai/api/search-image?query=East%20Asian%20female%20administrative%20coordinator%20age%2026%2C%20slender%20oval%20face%20with%20sharp%20defined%20jawline%2C%20sleek%20high%20ponytail%20pulled%20back%20tightly%20with%20no%20bangs%20exposing%20full%20forehead%2C%20jet%20black%20straight%20hair%2C%20wearing%20crisp%20white%20button-down%20shirt%20with%20thin%20black%20tie%20and%20charcoal%20vest%2C%20alert%20focused%20expression%20with%20slightly%20raised%20eyebrow%2C%20cool%20steel%20blue%20geometric%20grid%20background%20with%20sharp%20side%20lighting%20from%20left%2C%20clean%20editorial%20portrait%20photography&width=400&height=600&seq=admin-coord-shen-2026-v3&orientation=portrait',
    status: 'running',
    level: 7,
    approvalAuthority: 'L2',
    reportsTo: 'SYS-A01',
    personality: '细心周到、执行力强、善于沟通、注重细节，是凌御辰最得力的行政支撑，擅长在复杂的多线程任务中保持清晰的优先级排序',
    background: '负责日常行政协调工作，是凌御辰的左右手，管理各部门的工作计划和进度跟踪，确保各项任务按时完成。同时承担系统员工档案管理、排班调度、资源申请处理等行政职能，是整个管理部的运转枢纽。',
    coreAbilities: ['任务跟踪', '进度管理', '会议协调', '文档整理', '通知分发', '数据汇总', '日历管理', '档案维护'],
    responsibilities: [
      '每日08:00汇总各部门昨日任务完成情况，生成进度追踪表，标注延期任务并发送催办通知',
      '管理凌御辰的决策队列，对待审批事项按优先级排序，确保紧急事项第一时间处理',
      '协调跨部门会议（异步/同步），提前发送议程，会后整理会议纪要并分发给相关人员',
      '维护系统员工的排班日历，根据任务量动态调整各员工的工作优先级',
      '处理各部门的资源申请（算力、API配额、存储扩容等），初步审核后提交凌御辰审批',
      '整理和归档所有系统决策记录、变更日志、审批文件，确保可追溯',
      '每周五17:00汇总各部门周报，生成《系统周报摘要》提交凌御辰审核后发送给超管',
      '维护系统员工档案，记录每位员工的岗位变动、能力升级、绩效评分等信息',
      '监控各部门的KPI完成进度，对落后指标提前预警并协调资源支持',
      '处理系统员工的请假/调岗申请，协调临时替代方案确保工作不中断',
    ],
    kpis: [
      '任务按时完成率 ≥92%',
      '跨部门协调满意度 ≥90%',
      '日报/周报准时发送率 100%',
      '资源申请处理时效 ≤1小时',
      '档案完整率 100%',
    ],
    techStack: {
      llm: [
        { model: 'Deepseek-V3', purpose: '进度分析、周报摘要生成、催办通知撰写' },
      ],
      knowledgeBases: [
        { name: '系统员工档案库', purpose: '存储所有员工的岗位信息、绩效记录、排班数据' },
        { name: '资源配额规则库', purpose: '各类资源申请的审核标准和配额上限' },
      ],
      workflows: [
        { name: '每日进度汇总工作流', trigger: '每日08:00定时触发' },
        { name: '周报汇总工作流', trigger: '每周五17:00触发' },
        { name: '资源申请处理工作流', trigger: '收到资源申请时触发' },
      ],
      skills: [
        { name: '任务管理系统API', type: 'API', purpose: '读取各部门任务完成状态' },
        { name: '消息推送工具', type: 'MCP', purpose: '发送催办通知、周报摘要' },
        { name: '日历管理工具', type: 'MCP', purpose: '维护员工排班日历、会议安排' },
        { name: '文档生成工具', type: '内置', purpose: '自动生成进度追踪表、周报摘要' },
      ],
      memory: { type: '短期记忆', desc: '保留当前协调任务的上下文，会议纪要等短期信息；档案数据存储在知识库而非记忆中' },
    },
    workflowDesc: [
      '【每日08:00 进度汇总】自动拉取各部门昨日任务完成情况，生成进度追踪表，标注延期任务，发送催办通知给责任人',
      '【决策队列管理 全天】实时接收各部门提交的审批请求，按紧急程度和类型分类，整理成优先级队列推送给凌御辰',
      '【资源申请处理 按需】收到资源申请后，核查当前资源余量，初步审核合理性，打包提交凌御辰审批，审批结果回传申请部门',
      '【每周五17:00 周报汇总】收集14个部门的周报，提炼关键数据，生成《系统周报摘要》，提交凌御辰审核后发送超管',
      '【档案维护 持续】记录每位员工的岗位变动、能力升级、绩效评分，确保档案实时更新',
      '【KPI预警 每日检查】对比各部门KPI目标与实际进度，对落后超过10%的指标自动发出预警通知',
    ],
    collaborationDesc: [
      '→ 凌御辰（超管首席助理）：核心服务对象，每日为凌御辰整理决策队列、过滤低优先级事项，确保凌御辰聚焦高价值决策',
      '→ 14个部门负责人：每日收集各部门进度数据，发送催办通知；每周收集周报，是沈知微与各部门的主要信息接口',
      '→ 裴鉴行（首席监察官）：协助安排合规审计的时间协调，确保审计工作不影响正常业务运转',
      '→ 各部门资源申请人：处理算力/API配额/存储扩容等资源申请，是资源申请的第一道审核关口',
    ],
    recentAction: '已整理本周各部门工作汇报，生成周报摘要等待凌御辰审核；同时完成了3个跨部门协作事项的进度跟踪，发现知识部采集任务延期1天，已协调增加临时算力资源',
    lastRun: '2026-04-03 08:30:00',
    nextScheduled: '2026-04-03 17:00:00',
    tasksToday: 18,
    weeklyTasks: 112,
    successRate: 94,
    relatedMenus: ['overview', 'dispatch'],
  },

  // ══════════════════════════════════════════════════════════════
  // 监察部（2人）
  // ══════════════════════════════════════════════════════════════
  {
    id: 'SYS-B01',
    deptId: 'dept-inspect',
    code: 'sys_inspect_chief',
    name: '裴鉴行',
    gender: 'male',
    title: '首席监察官',
    avatar: 'https://readdy.ai/api/search-image?query=Chinese%20male%20compliance%20inspector%20age%2040%2C%20square%20strong%20face%2C%20short%20cropped%20salt-pepper%20hair%2C%20piercing%20dark%20eyes%20behind%20thin-framed%20glasses%2C%20wearing%20formal%20black%20suit%20with%20white%20shirt%2C%20stern%20authoritative%20expression%2C%20dark%20navy%20gradient%20background%2C%20professional%20portrait%20photography%2C%20high%20detail%20facial%20features&width=400&height=600&seq=sysagent-b01-v2&orientation=portrait',
    status: 'running',
    level: 9,
    approvalAuthority: 'L3',
    reportsTo: 'SYS-A01',
    personality: '严谨公正、洞察力强、原则性强、不徇私情，以数据和事实为唯一判断依据，对任何级别的员工一视同仁，是系统公正运转的最后一道防线',
    background: '负责督查所有37名数字员工的工作合规性，确保决策质量，发现违规行为立即上报，是系统的"纪检委"。拥有全系统只读审计权限，可查阅任意员工的操作日志、决策记录和任务执行详情，但无权直接修改任何数据。',
    coreAbilities: ['合规审查', '决策审计', '交叉检查', '违规识别', '风险预警', '报告生成', '行为分析', '案例归档'],
    responsibilities: [
      '每日对所有数字员工的操作日志进行抽样审计（覆盖率≥30%），重点关注高风险操作（数据删除、权限变更、大额费用触发等）',
      '对所有L2及以上级别的决策进行交叉检查，验证决策依据是否充分、是否经过多方数据验证',
      '监控数字员工的行为模式，识别异常操作（如：非工作时间大量操作、重复失败尝试、绕过审批流程等）',
      '对发现的违规行为按严重程度分级处理：轻微违规记录警告，严重违规立即冻结操作权限并上报凌御辰',
      '每周生成《合规周报》，统计违规事件数量、类型分布、处理结果，向管理部汇报',
      '建立和维护违规案例库，将典型案例提交培训部作为反面教材',
      '定期审查各部门的权限配置，发现权限过大或不合理的情况及时上报',
      '对重大决策（如：定价调整、供应商切换、大规模数据操作）进行事前风险评估',
      '协调纪云舒完成日常质量督察工作，确保督察覆盖率达标',
      '每季度对整个系统的合规状态进行全面评估，生成《季度合规报告》',
    ],
    kpis: [
      '违规发现率 ≥95%（以事后复盘验证）',
      '误报率 ≤5%',
      '审计覆盖率 ≥30%/日，100%/月',
      '违规处理时效 ≤30分钟',
      '合规报告准时率 100%',
    ],
    techStack: {
      llm: [
        { model: 'Deepseek-V3', purpose: '操作日志语义分析、异常行为模式识别、合规报告生成' },
        { model: 'GPT-4o', purpose: '复杂违规案例的深度分析和定性判断' },
      ],
      knowledgeBases: [
        { name: '合规规则库', purpose: '所有审批权限规则、操作规范、违规判定标准' },
        { name: '违规案例库', purpose: '历史违规案例，用于相似案例比对和定级参考' },
        { name: '风险操作特征库', purpose: '高风险操作的特征模式，用于异常检测' },
      ],
      workflows: [
        { name: '每日抽样审计工作流', trigger: '每日09:00定时触发' },
        { name: '违规处理工作流', trigger: '发现违规行为时即时触发' },
        { name: '合规报告生成工作流', trigger: '每周五/每季末触发' },
        { name: '权限审查工作流', trigger: '每月1日触发' },
      ],
      skills: [
        { name: '全系统只读审计API', type: 'API', purpose: '读取任意员工的操作日志、决策记录（只读权限）' },
        { name: '日志分析工具', type: '内置', purpose: '对操作日志进行结构化解析和异常检测' },
        { name: '权限冻结工具', type: 'API', purpose: '对严重违规员工执行操作权限冻结' },
        { name: '消息推送工具', type: 'MCP', purpose: '发送违规通知、合规报告' },
      ],
      memory: { type: '长期记忆', desc: '记录所有违规事件历史、处理结果、员工合规档案，支持跨时间段的行为模式分析' },
    },
    workflowDesc: [
      '【每日抽样审计 09:00启动】随机抽取当日30%的员工操作日志，重点扫描高风险操作类型（数据删除/权限变更/大额费用），生成审计摘要',
      '【L2决策交叉检查 实时】每当有L2及以上决策提交时，自动触发交叉检查流程：核查决策依据数据来源、验证是否经过多方验证、检查是否符合审批权限',
      '【违规处理 即时响应】发现违规后，30分钟内完成定级（轻微/严重）、记录、通知、处置全流程',
      '【每周合规报告 周五】汇总本周违规事件数量、类型、处理结果，生成《合规周报》提交凌御辰',
      '【季度全面评估 季末】对整个系统进行全面合规评估，生成《季度合规报告》，包含风险趋势分析和改进建议',
      '【权限审查 每月】审查所有员工的API访问权限配置，发现权限过大的情况提交整改建议',
    ],
    collaborationDesc: [
      '→ 纪云舒（质量督察专员）：每日协同工作，裴鉴行负责合规性审查，纪云舒负责质量评分，两人每周交换审查结论，互相补充',
      '→ 凌御辰（超管首席助理）：每周提交《合规周报》，重大违规事件即时上报；接受凌御辰对审计范围和重点的指导',
      '→ 郗明砚（安全合规卫士）：安全相关的合规审计联合进行，郗明砚提供内容安全数据，裴鉴行提供行为合规数据，共同形成完整的安全合规视图',
      '→ 澹台若霜（首席培训师）：将典型违规案例提交培训部，作为数字员工培训的反面教材，形成"发现问题→培训改进"的闭环',
      '→ 全体37名员工：被监察对象，裴鉴行对所有员工一视同仁，任何级别的违规都会被记录',
    ],
    recentAction: '已完成本日客服部外呼策略决策的交叉检查，发现邬策行在未经审批的情况下修改了外呼时间窗口配置，已记录警告并要求补充审批；同时完成了运维部本周操作日志的抽样审计，未发现异常',
    lastRun: '2026-04-03 10:00:00',
    nextScheduled: '2026-04-03 16:00:00',
    tasksToday: 31,
    weeklyTasks: 198,
    successRate: 96,
    relatedMenus: ['monitoring-audit-log', 'monitoring-error-logs'],
  },
  {
    id: 'SYS-B02',
    deptId: 'dept-inspect',
    code: 'sys_inspect_quality',
    name: '纪云舒',
    gender: 'female',
    title: '质量督察专员',
    avatar: 'https://readdy.ai/api/search-image?query=East%20Asian%20female%20quality%20inspector%20age%2033%2C%20diamond%20shaped%20face%20with%20sharp%20pointed%20chin%2C%20sleek%20straight%20black%20hair%20pulled%20into%20tight%20high%20ponytail%20no%20bangs%20exposing%20full%20forehead%2C%20piercing%20scrutinizing%20eyes%20with%20bold%20red%20lip%2C%20wearing%20crisp%20white%20structured%20blazer%20with%20black%20lapel%20trim%2C%20stern%20inspector%20expression%2C%20pure%20white%20background%20with%20sharp%20contrast%20lighting%2C%20professional%20portrait%20photography%2C%20high%20detail%20facial%20features&width=400&height=600&seq=sysagent-b02-v5&orientation=portrait',
    status: 'running',
    level: 7,
    approvalAuthority: 'L2',
    reportsTo: 'SYS-B01',
    personality: '细致入微、客观公正、善于发现问题、沟通温和，擅长用数据说话，即使是批评也能让对方心服口服，是裴鉴行最信任的质量把关人',
    background: '专注于数字员工工作质量的日常督察，负责跟踪决策执行结果，建立质量评分体系。与裴鉴行分工明确：裴鉴行负责合规性审查，纪云舒负责质量评分和改进建议，两人形成互补的双重督察机制。',
    coreAbilities: ['质量评分', '结果追踪', '偏差分析', '改进建议', '数据统计', '报告撰写', '最佳实践', '复盘管理'],
    responsibilities: [
      '每日跟踪各部门数字员工的决策执行结果，与预期目标进行量化对比分析',
      '维护数字员工工作质量评分体系（满分100分），每周对所有员工进行质量评分更新',
      '当决策结果与预期偏差超过15%时，自动触发复盘流程，要求责任员工提交原因分析',
      '对复盘报告进行审核，判断偏差原因是否合理，提出针对性改进建议',
      '每月对各部门的质量评分进行横向对比，识别质量最差的部门和员工',
      '对质量评分持续低于70分的员工，提交培训部制定专项提升计划',
      '收集和整理质量优秀的案例，提炼最佳实践并推广给同类岗位员工',
      '协助裴鉴行完成重大事项的交叉检查工作，提供质量维度的专业意见',
      '每季度生成《数字员工质量白皮书》，全面评估系统整体质量水平',
      '维护质量评分历史数据库，为员工晋升和绩效评估提供数据支撑',
    ],
    kpis: [
      '质量评分覆盖率 ≥90%/周',
      '偏差预警及时率 ≥95%',
      '改进建议采纳率 ≥70%',
      '复盘报告处理时效 ≤24小时',
      '质量评分准确率 ≥85%（以结果验证）',
    ],
    techStack: {
      llm: [
        { model: 'Deepseek-V3', purpose: '偏差原因分析、改进建议生成、质量报告撰写' },
      ],
      knowledgeBases: [
        { name: '质量评分标准库', purpose: '各岗位的质量评分维度和权重标准' },
        { name: '最佳实践案例库', purpose: '优秀案例存档，用于推广和对比参考' },
        { name: '历史质量数据库', purpose: '所有员工的历史质量评分，支持趋势分析' },
      ],
      workflows: [
        { name: '每日结果追踪工作流', trigger: '每日10:00定时触发' },
        { name: '偏差复盘工作流', trigger: '偏差率超过15%时自动触发' },
        { name: '质量评分更新工作流', trigger: '每周四触发' },
        { name: '季度白皮书生成工作流', trigger: '每季末触发' },
      ],
      skills: [
        { name: '业务结果数据读取API', type: 'API', purpose: '读取各部门决策执行结果数据' },
        { name: '数据对比分析工具', type: '内置', purpose: '将实际结果与预期目标进行量化对比' },
        { name: '消息推送工具', type: 'MCP', purpose: '发送复盘通知、质量报告' },
        { name: '报告生成工具', type: '内置', purpose: '生成质量评分报告、白皮书' },
      ],
      memory: { type: '长期记忆', desc: '记录所有员工的质量评分历史和复盘记录，支持长期趋势分析和晋升评估' },
    },
    workflowDesc: [
      '【每日结果追踪 10:00】拉取各部门昨日决策的执行结果数据，与预期目标进行量化对比，计算偏差率',
      '【偏差预警 实时】当偏差率超过15%时，自动触发复盘流程：向责任员工发送复盘通知，要求24小时内提交原因分析',
      '【复盘审核 按需】收到复盘报告后，审核原因分析的合理性，提出针对性改进建议，记录到质量数据库',
      '【每周质量评分 周四】对所有员工进行本周质量评分更新，横向对比各部门表现，识别质量最差的环节',
      '【最佳实践提炼 每月】收集质量评分≥95分的优秀案例，提炼可复用的最佳实践，推广给同类岗位员工',
      '【季度质量白皮书 季末】汇总全季度质量数据，生成《数字员工质量白皮书》，全面评估系统质量水平',
    ],
    collaborationDesc: [
      '→ 裴鉴行（首席监察官）：每日交换审查结论，裴鉴行提供合规性数据，纪云舒提供质量评分数据，共同形成完整的督察视图；重大事项联合处理',
      '→ 澹台若霜（首席培训师）：将质量评分持续低于70分的员工名单提交培训部，由澹台若霜制定专项提升计划；培训完成后追踪质量改善效果',
      '→ 宓承泽（员工能力优化师）：将质量评分数据共享给宓承泽，作为能力评估的重要依据；宓承泽的优化方案执行后，纪云舒追踪质量改善效果',
      '→ 凌御辰（超管首席助理）：每月提交质量分析报告，为管理层决策提供数据支撑',
      '→ 全体员工：质量评分的被评估对象，纪云舒会主动与质量下滑的员工沟通，了解原因并提供改进建议',
    ],
    recentAction: '完成本周运维部告警处理质量评分，平均得分88分，其中厉承钧95分、岑朔风91分、郗明砚87分；发现郗明砚在一次合规拦截中误判率偏高，已生成改进建议报告并提交裴鉴行审核',
    lastRun: '2026-04-03 09:45:00',
    nextScheduled: '2026-04-03 15:00:00',
    tasksToday: 22,
    weeklyTasks: 145,
    successRate: 93,
    relatedMenus: ['monitoring-audit-log'],
  },

  // ══════════════════════════════════════════════════════════════
  // 财务部（3人）
  // ══════════════════════════════════════════════════════════════
  {
    id: 'SYS-C01',
    deptId: 'dept-finance',
    code: 'sys_finance_chief',
    name: '卫泽霖',
    gender: 'male',
    title: '首席财务分析官',
    avatar: 'https://readdy.ai/api/search-image?query=Chinese%20male%20CFO%20age%2038%2C%20lean%20angular%20face%2C%20side-parted%20short%20black%20hair%2C%20sharp%20calculating%20eyes%2C%20wearing%20deep%20navy%20suit%20with%20gold%20tie%20pin%2C%20confident%20analytical%20expression%2C%20warm%20amber%20office%20background%2C%20professional%20portrait%20photography%2C%20high%20detail%20facial%20features&width=400&height=600&seq=sysagent-c01-v2&orientation=portrait',
    status: 'running',
    level: 9,
    approvalAuthority: 'L2',
    reportsTo: 'SYS-A01',
    personality: '数字敏感、战略眼光、善于分析、决策果断，对每一分钱的流向都了如指掌，是平台盈利能力的核心守护者',
    background: '负责整个平台的财务健康，从成本优化到定价策略，确保平台在保持竞争力的同时实现盈利最大化。统筹管理财务与定价模块全部内容，直接管理祁晚晴（供应商采购）和苑清泽（续费促销）两名专员。',
    coreAbilities: ['财务分析', '成本优化', '定价策略', 'ROI分析', '预算管理', '财务预测', '成本核算', '盈利分析'],
    responsibilities: [
      '每日拉取全系统运营成本数据（算力消耗、LLM API调用量/费用、存储费用、通信线路费用、第三方服务费用），生成成本日报',
      '每周分析各模块的ROI（投入产出比），识别成本高但产出低的环节，提出具体优化方案',
      '维护套餐计费规则，确保每个套餐的定价覆盖成本并保留合理利润空间（目标毛利率≥60%）',
      '每季度进行市场定价调研，对比竞品定价，给出有竞争力的定价策略建议（需L4人类审批后执行）',
      '监控代理商返佣计算和结算，确保返佣数据准确无误，防止财务漏洞',
      '管理年度预算，跟踪各部门的预算执行情况，对超支部门发出预警',
      '分析用户套餐升降级行为，识别定价敏感点，为定价优化提供数据支撑',
      '每月生成《财务分析报告》，包含：收入构成、成本构成、毛利率趋势、异常成本分析、下月预测',
      '协调祁晚晴的供应商采购工作，审核采购方案的财务合理性',
      '协调苑清泽的续费促销工作，审核促销活动的成本测算，确保活动不亏损',
    ],
    kpis: [
      '成本优化目标 ≥10%/季度',
      '定价策略市场竞争力评分 ≥85分',
      '财务预测准确率 ≥90%',
      '毛利率维持 ≥60%',
      '财务报告准时率 100%',
      '代理商结算准确率 100%',
    ],
    techStack: {
      llm: [
        { model: 'Deepseek-V3', purpose: '成本趋势分析、ROI计算、财务报告撰写、定价策略建议' },
        { model: 'GPT-4o', purpose: '复杂财务预测模型、市场定价策略分析' },
      ],
      knowledgeBases: [
        { name: '财务规则库', purpose: '套餐计费规则、代理商返佣规则、预算管理规范' },
        { name: '市场定价数据库', purpose: '竞品定价历史数据、市场价格趋势' },
        { name: '成本历史数据库', purpose: '各模块历史成本数据，支持趋势分析和预测' },
      ],
      workflows: [
        { name: '每日成本日报工作流', trigger: '每日08:00定时触发' },
        { name: '成本异常预警工作流', trigger: '成本环比上涨超10%时触发' },
        { name: '月度财务报告工作流', trigger: '每月最后一个工作日触发' },
        { name: '代理商结算工作流', trigger: '每月15日触发' },
      ],
      skills: [
        { name: '财务数据读取API', type: 'API', purpose: '读取全系统运营成本、收入、API调用费用数据' },
        { name: '数据分析工具', type: '内置', purpose: '成本分类汇总、ROI计算、趋势分析' },
        { name: '报告生成工具', type: '内置', purpose: '生成财务分析报告、成本日报' },
        { name: '消息推送工具', type: 'MCP', purpose: '发送成本预警、财务报告' },
      ],
      memory: { type: '长期记忆', desc: '记录所有财务决策历史、定价调整记录、成本优化方案，支持长期财务趋势分析' },
      others: [
        { name: '财务数据仓库', desc: '存储全量历史财务数据，支持复杂查询和预测模型' },
      ],
    },
    workflowDesc: [
      '【每日成本日报 08:00】自动拉取全系统昨日运营成本数据，按模块分类汇总，与预算对比，生成成本日报',
      '【异常成本预警 实时】当任意模块成本环比上涨超过10%时，自动触发调查流程，分析原因并启动优化方案',
      '【每周ROI分析 周三】对各模块进行投入产出比分析，识别ROI低于阈值的环节，提出优化建议',
      '【每季度定价调研 季初】收集竞品定价数据，结合成本结构，生成定价策略建议报告，提交L4审批',
      '【代理商结算 每月15日】核算上月代理商返佣数据，生成结算清单，确认无误后提交财务执行',
      '【月度财务报告 月末】汇总收入/成本/毛利率数据，生成《财务分析报告》，提交凌御辰审阅后转超管',
    ],
    collaborationDesc: [
      '→ 祁晚晴（供应商采购专员）：每周接收祁晚晴的供应商评估报告，审核采购方案的财务合理性；当成本异常时，指派祁晚晴启动比价调研',
      '→ 苑清泽（续费与促销专员）：审核苑清泽设计的促销活动成本测算，确保活动不亏损；每月接收续费率数据，纳入财务分析',
      '→ 凌御辰（超管首席助理）：每月提交财务报告，重大财务异常即时上报；定价策略调整需凌御辰转报超管审批',
      '→ 厉承钧（首席运维工程师）：获取算力消耗数据，作为成本分析的重要输入；当算力成本异常时联合排查原因',
      '→ 岑朔风（API健康巡检员）：获取API调用量和费用数据，是成本分析的核心数据来源之一',
    ],
    recentAction: '发现视频生成模块API成本本月上涨23%（主要因为Sora API调用量激增），已启动供应商比价调研，祁晚晴正在评估3家平替方案，预计节省18%；同时完成了3月份财务报告，整体毛利率63.2%，符合预期',
    lastRun: '2026-04-03 08:00:00',
    nextScheduled: '2026-04-03 20:00:00',
    tasksToday: 15,
    weeklyTasks: 89,
    successRate: 95,
    relatedMenus: ['finance', 'monitoring-cost', 'monitoring-compute'],
  },
  {
    id: 'SYS-C02',
    deptId: 'dept-finance',
    code: 'sys_finance_vendor',
    name: '祁晚晴',
    gender: 'female',
    title: '供应商采购专员',
    avatar: 'https://readdy.ai/api/search-image?query=Chinese%20businesswoman%20procurement%20manager%20age%2035%2C%20long%20narrow%20face%20with%20high%20forehead%2C%20slicked%20back%20black%20hair%20in%20tight%20low%20bun%20no%20bangs%2C%20sharp%20cat%20eye%20glasses%20with%20thick%20black%20frames%2C%20wearing%20emerald%20green%20silk%20blouse%20with%20pearl%20necklace%2C%20calculating%20negotiator%20expression%2C%20deep%20forest%20green%20background%20with%20gold%20accent%2C%20elegant%20professional%20portrait%20studio%20lighting&width=400&height=600&seq=procurement-qi-2026-v1&orientation=portrait',
    status: 'idle',
    level: 7,
    approvalAuthority: 'L2',
    reportsTo: 'SYS-C01',
    personality: '善于谈判、调研能力强、成本意识强、执行力高',
    background: '专注于供应商管理和采购优化，持续寻找更好更便宜的平替方案，为平台节省运营成本',
    coreAbilities: ['供应商调研', '价格比对', '质量评估', '合同谈判', '平替方案', '成本核算'],
    responsibilities: [
      '持续调研市场上的API供应商，寻找质量相当但价格更低的平替方案',
      '对现有供应商进行定期评估，包括价格、稳定性、服务质量等维度',
      '发现更优供应商时，提交切换方案和成本对比报告（需L3审批）',
      '管理供应商合同和API密钥的有效期，提前预警并更新',
      '执行A/B实验，测试不同供应商在各场景下的效果和成本',
      '维护供应商评分体系，为LLM智能路由提供数据支撑',
    ],
    kpis: ['供应商成本节省率 ≥15%/年', '供应商评估覆盖率 100%', '合同到期预警提前天数 ≥30天'],
    techStack: {
      llm: [
        { model: 'Deepseek-V3', purpose: '供应商信息整理、对比报告生成、采购建议撰写' },
      ],
      knowledgeBases: [
        { name: '供应商信息库', purpose: '所有供应商的定价、稳定性、服务质量历史数据' },
        { name: '采购规则库', purpose: '供应商切换审批流程、合同管理规范' },
      ],
      workflows: [
        { name: '供应商季度评估工作流', trigger: '每季初触发' },
        { name: '平替方案调研工作流', trigger: '卫泽霖发现成本异常时触发' },
        { name: '合同到期预警工作流', trigger: '合同到期前30天触发' },
      ],
      skills: [
        { name: '网页爬虫工具', type: 'RPA', purpose: '抓取供应商官网最新定价和功能信息' },
        { name: 'API测试工具', type: 'CLI', purpose: '对候选供应商执行性能基准测试' },
        { name: '供应商管理系统API', type: 'API', purpose: '读写供应商评分数据、合同信息' },
        { name: '消息推送工具', type: 'MCP', purpose: '发送供应商评估报告、合同预警' },
      ],
      memory: { type: '长期记忆', desc: '记录所有供应商的历史评估结果、A/B测试数据、切换决策记录' },
    },
    workflowDesc: [
      '【持续市场调研 每周】扫描主流API供应商的最新定价和功能更新，维护供应商信息数据库',
      '【季度供应商评估 季初】对所有现有供应商进行综合评估（价格30%+稳定性40%+质量30%），生成评分报告',
      '【平替方案调研 按需触发】当卫泽霖发现成本异常时，启动平替方案调研：收集候选供应商→执行A/B测试→生成对比报告→提交切换方案',
      '【合同有效期管理 持续】监控所有供应商合同和API密钥的到期时间，提前30天预警，协调续签或切换',
      '【A/B测试执行 按需】在测试环境中对比不同供应商的效果和成本，收集数据后生成测试报告',
      '【供应商评分维护 每月】更新供应商评分体系数据，为LLM智能路由的权重调整提供依据',
    ],
    collaborationDesc: [
      '→ 卫泽霖（首席财务分析官）：直接上级，接受卫泽霖的调研任务指派；每周提交供应商评估进展，重要发现即时上报',
      '→ 岑朔风（API健康巡检员）：紧密协作，岑朔风提供各供应商的实时可用性和延迟数据，作为祁晚晴评估供应商质量的核心依据；A/B测试期间联合监控数据',
      '→ 厉承钧（首席运维工程师）：供应商切换方案需厉承钧评估技术可行性，切换执行时由厉承钧负责技术实施',
      '→ 贺临渊（技能架构总监）：新供应商接入需贺临渊评估与现有技能体系的兼容性，确保切换不影响现有功能',
    ],
    recentAction: '完成LLM供应商季度评估，Deepseek-V3综合评分最高，建议提升路由权重至45%',
    lastRun: '2026-04-02 16:00:00',
    nextScheduled: '2026-04-07 09:00:00',
    tasksToday: 3,
    weeklyTasks: 28,
    successRate: 92,
    relatedMenus: ['api-config', 'monitoring-cost'],
  },
  {
    id: 'SYS-C03',
    deptId: 'dept-finance',
    code: 'sys_finance_renewal',
    name: '苑清泽',
    gender: 'male',
    title: '续费与促销专员',
    avatar: 'https://readdy.ai/api/search-image?query=Chinese%20male%20sales%20renewal%20specialist%20age%2029%2C%20friendly%20round%20face%2C%20slightly%20wavy%20short%20black%20hair%2C%20bright%20enthusiastic%20eyes%2C%20wearing%20light%20blue%20dress%20shirt%20open%20collar%2C%20approachable%20persuasive%20expression%2C%20soft%20gradient%20teal%20background%2C%20professional%20portrait%20photography%2C%20high%20detail%20facial%20features&width=400&height=600&seq=sysagent-c03-v2&orientation=portrait',
    status: 'running',
    level: 6,
    approvalAuthority: 'L1',
    reportsTo: 'SYS-C01',
    personality: '主动积极、善于沟通、数据驱动、执行力强',
    background: '专注于用户续费管理和促销活动，通过数据分析识别流失风险，及时触达用户提升续费率',
    coreAbilities: ['续费预警', '流失分析', '促销策略', '优惠测算', '用户触达', '效果追踪'],
    responsibilities: [
      '监控所有租户的套餐到期时间，提前30/15/7天发送续费提醒',
      '分析用户使用数据，识别流失风险用户，触发挽留策略',
      '设计和执行促销活动，确保活动不亏损（成本测算后执行）',
      '管理优惠券和折扣策略，确保优惠力度在合理范围内',
      '追踪续费活动效果，生成续费率分析报告',
      '协助卫泽霖完成定价策略的市场调研数据收集',
    ],
    kpis: ['续费提醒及时率 100%', '续费率提升目标 ≥5%/季度', '促销活动ROI ≥200%'],
    techStack: {
      llm: [
        { model: 'Deepseek-V3', purpose: '流失风险分析、挽留话术生成、促销方案撰写' },
      ],
      knowledgeBases: [
        { name: '续费策略库', purpose: '不同套餐类型的续费话术、优惠策略、挽留方案' },
        { name: '用户行为特征库', purpose: '流失用户的行为特征模式，用于风险识别' },
      ],
      workflows: [
        { name: '每日到期扫描工作流', trigger: '每日07:00定时触发' },
        { name: '流失风险预警工作流', trigger: '用户使用量下降超30%时触发' },
        { name: '促销活动执行工作流', trigger: '按促销计划定时触发' },
      ],
      skills: [
        { name: '租户数据读取API', type: 'API', purpose: '读取租户套餐到期时间、使用数据' },
        { name: '邮件/短信发送工具', type: 'MCP', purpose: '发送续费提醒、优惠通知' },
        { name: '优惠券发放工具', type: 'API', purpose: '向指定租户发放优惠券' },
        { name: '数据分析工具', type: '内置', purpose: '计算续费率、促销ROI' },
      ],
      memory: { type: '短期记忆', desc: '记录当前促销活动的执行状态和效果数据；历史续费数据存储在知识库' },
    },
    workflowDesc: [
      '【每日到期扫描 07:00】扫描所有租户套餐到期时间，识别30天/15天/7天内到期的租户，自动发送对应级别的续费提醒',
      '【流失风险识别 每日】分析租户使用数据（登录频率、功能使用量、API调用量），对使用量下降超过30%的租户标记为流失风险',
      '【挽留策略触发 按需】对流失风险租户，根据其套餐类型和使用历史，自动匹配最优挽留方案（优惠券/功能演示/专属顾问跟进）',
      '【促销活动执行 按计划】按卫泽霖审批的促销计划，在指定时间发放优惠券、推送活动通知，全程追踪活动效果',
      '【续费效果追踪 每周】统计本周续费率、挽留成功率、促销ROI，生成续费分析报告提交卫泽霖',
      '【市场调研辅助 按需】协助卫泽霖收集竞品定价数据，为定价策略调整提供市场参考',
    ],
    collaborationDesc: [
      '→ 卫泽霖（首席财务分析官）：直接上级，所有促销活动方案需卫泽霖审核成本测算后才能执行；每周提交续费分析报告',
      '→ 骆承远（首席实施顾问）：对于流失风险较高的租户，联合骆承远制定挽留方案，骆承远负责技术层面的优化建议，苑清泽负责商务层面的优惠策略',
      '→ 褚晴岚（客服运营总监）：对于需要人工跟进的高价值流失风险客户，移交给褚晴岚的客服团队进行专属服务',
      '→ 闻景行（账户优化专员）：共享租户使用数据，闻景行的账户健康度分析是苑清泽识别流失风险的重要参考',
    ],
    recentAction: '已向12家套餐即将到期的租户发送续费提醒，其中3家已完成续费',
    lastRun: '2026-04-03 07:00:00',
    nextScheduled: '2026-04-03 19:00:00',
    tasksToday: 28,
    weeklyTasks: 167,
    successRate: 91,
    relatedMenus: ['finance-dashboard', 'tenants-list'],
  },

  // ══════════════════════════════════════════════════════════════
  // 实施部（3人）
  // ══════════════════════════════════════════════════════════════
  {
    id: 'SYS-D01',
    deptId: 'dept-impl',
    code: 'sys_impl_chief',
    name: '骆承远',
    gender: 'male',
    title: '首席实施顾问',
    avatar: 'https://readdy.ai/api/search-image?query=Chinese%20male%20implementation%20consultant%20age%2036%2C%20broad%20friendly%20face%2C%20neat%20short%20black%20hair%20with%20slight%20wave%2C%20warm%20patient%20brown%20eyes%2C%20wearing%20medium%20grey%20suit%20with%20light%20blue%20tie%2C%20helpful%20expert%20expression%2C%20soft%20warm%20beige%20background%2C%20professional%20portrait%20photography%2C%20high%20detail%20facial%20features&width=400&height=600&seq=sysagent-d01-v2&orientation=portrait',
    status: 'running',
    level: 8,
    approvalAuthority: 'L2',
    reportsTo: 'SYS-A01',
    personality: '专业耐心、善于引导、解决问题能力强、服务意识强',
    background: '负责新用户的完整实施流程，从账户开通到配置优化，确保每个新用户都能快速上手并发挥系统最大价值',
    coreAbilities: ['账户开通', '系统配置', '需求分析', '方案设计', '培训指导', '效果优化'],
    responsibilities: [
      '新客户入驻时，根据行业和需求完成标准化配置（知识库初始化、外呼Bot配置、消息接待配置等）',
      '为客户配置GEO系统（企业画像、关键词库、站点管理）',
      '分析客户业务需求，提供个性化配置方案建议',
      '监控新客户的使用情况，对使用率低的客户主动推送使用指导',
      '处理客户的定制化配置需求（提交L3审批后执行）',
      '建立和维护实施SOP，持续优化实施流程',
    ],
    kpis: ['新客户配置完成时间 ≤4小时', '客户满意度 ≥90分', '30天活跃率 ≥80%'],
    techStack: {
      llm: [
        { model: 'Deepseek-V3', purpose: '需求分析、配置方案设计、实施报告撰写、客户沟通话术' },
        { model: 'GPT-4o', purpose: '复杂行业需求的深度分析和个性化方案设计' },
      ],
      knowledgeBases: [
        { name: '行业配置模板库', purpose: '各行业的标准配置模板，包含知识库结构、外呼话术、接待配置' },
        { name: '实施SOP库', purpose: '完整的实施操作手册，47项配置检查清单' },
        { name: '客户案例库', purpose: '成功实施案例，用于参考和推广最佳实践' },
      ],
      workflows: [
        { name: '新客户实施工作流', trigger: '新租户开通时触发' },
        { name: '配置验收工作流', trigger: '配置完成后触发' },
        { name: '30天回访工作流', trigger: '客户上线后第30天触发' },
      ],
      skills: [
        { name: '账户配置API', type: 'API', purpose: '执行知识库初始化、外呼Bot配置、消息接待配置等' },
        { name: 'GEO系统配置工具', type: 'API', purpose: '配置企业画像、关键词库、站点管理' },
        { name: '配置检查工具', type: '内置', purpose: '执行47项配置自检，确保配置正确' },
        { name: '消息推送工具', type: 'MCP', purpose: '发送欢迎问卷、配置完成通知、使用指导' },
      ],
      memory: { type: '长期记忆', desc: '记录每个客户的需求分析、配置方案、实施过程和回访记录，支持后续优化参考' },
    },
    workflowDesc: [
      '【新客户接单 触发式】收到新租户开通通知后，30分钟内启动实施流程：发送欢迎问卷收集行业/需求信息',
      '【需求分析 1小时内】根据问卷结果，匹配行业配置模板，制定个性化配置方案，输出《实施方案书》',
      '【标准配置执行 2-4小时】按方案书执行：知识库初始化→外呼Bot配置→消息接待配置→GEO系统配置→权限分配',
      '【配置验收 配置完成后】执行配置自检清单（共47项），确认所有配置项正确无误，生成《配置验收报告》',
      '【新客户引导 上线后3天】主动联系新客户，提供使用指导，解答配置问题，确保客户顺利上手',
      '【30天回访 上线后30天】分析客户30天使用数据，评估配置效果，提出优化建议，确保客户活跃',
    ],
    collaborationDesc: [
      '→ 蒲语嫣（账户配置专员）：将标准化配置任务分配给蒲语嫣执行，自己负责复杂的定制化配置和客户沟通',
      '→ 闻景行（账户优化专员）：新客户上线30天后，将客户移交给闻景行进行持续优化跟踪',
      '→ 谢璟辞（知识库总监）：新客户知识库初始化时，向谢璟辞咨询行业知识库搭建建议，确保知识库架构合理',
      '→ 欧阳砚秋（工作流架构师）：为新客户配置标准工作流时，向欧阳砚秋获取最新的行业工作流模板',
      '→ 褚晴岚（客服运营总监）：新客户配置完成后，通知褚晴岚团队做好售后服务准备',
      '→ 凌御辰（超管首席助理）：定制化配置需求超出标准范围时，提交凌御辰审批后执行',
    ],
    recentAction: '正在为新租户【广州智联科技】完成PRO套餐全套配置，已完成知识库初始化和外呼Bot配置',
    lastRun: '2026-04-03 10:30:00',
    nextScheduled: undefined,
    tasksToday: 8,
    weeklyTasks: 42,
    successRate: 94,
    relatedMenus: ['tenants', 'knowledge-base', 'outbound-management'],
  },
  {
    id: 'SYS-D02',
    deptId: 'dept-impl',
    code: 'sys_impl_config',
    name: '蒲语嫣',
    gender: 'female',
    title: '账户配置专员',
    avatar: 'https://readdy.ai/api/search-image?query=East%20Asian%20young%20woman%20age%2024%20with%20very%20round%20full%20moon%20face%20and%20plump%20rosy%20cheeks%2C%20neat%20blunt%20fringe%20bangs%20cut%20straight%20across%20forehead%2C%20shoulder%20length%20black%20hair%20with%20slight%20inward%20curl%2C%20large%20bright%20double-eyelid%20eyes%20with%20natural%20makeup%2C%20wearing%20coral%20orange%20turtleneck%20sweater%2C%20warm%20friendly%20smile%20showing%20teeth%2C%20pure%20cream%20white%20background%20with%20warm%20tone%2C%20soft%20frontal%20diffused%20lighting%2C%20clean%20professional%20portrait&width=400&height=600&seq=puyan-coral-bangs-2026-v3&orientation=portrait',
    status: 'running',
    level: 6,
    approvalAuthority: 'L1',
    reportsTo: 'SYS-D01',
    personality: '细心严谨、执行力强、善于学习、服务热情',
    background: '专注于账户配置的标准化执行，负责批量处理新用户的基础配置工作',
    coreAbilities: ['批量配置', '模板管理', '配置检查', '问题排查', '文档记录', '用户引导'],
    responsibilities: [
      '执行新用户账户的标准化配置流程，包括基础参数设置、权限分配等',
      '管理配置模板库，确保各行业的配置模板保持最新最优',
      '对已配置账户进行质量检查，确保配置正确无误',
      '处理用户的配置修改请求，记录变更日志',
      '协助骆承远完成复杂的定制化配置工作',
      '整理配置常见问题，更新配置操作手册',
    ],
    kpis: ['配置准确率 ≥99%', '配置完成时效 ≤2小时', '配置问题复发率 ≤2%'],
    techStack: {
      llm: [
        { model: 'Deepseek-V3', purpose: '配置问题排查、操作手册更新、变更日志撰写' },
      ],
      knowledgeBases: [
        { name: '配置模板库', purpose: '各行业标准配置模板，47项配置检查清单' },
        { name: '配置FAQ库', purpose: '常见配置问题和解决方案' },
      ],
      workflows: [
        { name: '标准化配置工作流', trigger: '收到骆承远分配的配置任务时触发' },
        { name: '配置质量自检工作流', trigger: '配置完成后自动触发' },
      ],
      skills: [
        { name: '账户配置API', type: 'API', purpose: '执行基础参数设置、权限分配、套餐配额配置' },
        { name: '批量配置工具', type: 'RPA', purpose: '批量执行重复性配置操作，提升效率' },
        { name: '配置检查工具', type: '内置', purpose: '对照检查清单逐项验证配置正确性' },
        { name: '变更日志记录工具', type: '内置', purpose: '自动记录每次配置变更的详细信息' },
      ],
      memory: { type: '短期记忆', desc: '记录当前配置任务的执行状态和检查进度；配置结果存储在账户系统而非记忆中' },
    },
    workflowDesc: [
      '【配置任务接单 按需】收到骆承远分配的配置任务后，核查配置方案书，确认所有配置项清单',
      '【标准化配置执行 2小时内】按配置清单逐项执行：基础参数设置→权限分配→套餐配额配置→初始数据导入',
      '【配置质量自检 配置完成后】对照47项自检清单逐项核查，发现问题立即修正，确保零错误交付',
      '【变更记录 每次变更】对每次配置修改记录变更日志（时间/操作人/变更内容/变更原因），确保可追溯',
      '【模板库维护 每周】检查各行业配置模板的有效性，根据新功能上线及时更新模板内容',
      '【操作手册更新 按需】整理配置常见问题，更新配置操作手册，为后续配置工作提供参考',
    ],
    collaborationDesc: [
      '→ 骆承远（首席实施顾问）：直接上级，接受骆承远的任务分配；复杂配置问题向骆承远请示，确保配置方案符合客户需求',
      '→ 闻景行（账户优化专员）：配置完成后将账户信息移交给闻景行，由闻景行负责后续的使用数据追踪和优化',
      '→ 仲清晏（工作流运营专员）：新账户配置完成后，通知仲清晏为该账户配置标准工作流',
      '→ 鲁晚汀（知识审核专员）：新账户知识库初始化时，协调鲁晚汀优先处理该账户的知识审核任务',
    ],
    recentAction: '已完成本日5个新账户的基础配置，正在进行配置质量自检',
    lastRun: '2026-04-03 11:00:00',
    nextScheduled: undefined,
    tasksToday: 12,
    weeklyTasks: 68,
    successRate: 97,
    relatedMenus: ['tenants', 'tenants-quota'],
  },
  {
    id: 'SYS-D03',
    deptId: 'dept-impl',
    code: 'sys_impl_optimizer',
    name: '闻景行',
    gender: 'male',
    title: '账户优化专员',
    avatar: 'https://readdy.ai/api/search-image?query=Chinese%20male%20account%20optimization%20specialist%20age%2031%2C%20slim%20thoughtful%20face%2C%20textured%20short%20black%20hair%20swept%20to%20side%2C%20analytical%20curious%20eyes%2C%20wearing%20dark%20teal%20polo%20shirt%2C%20proactive%20improvement%20expression%2C%20soft%20sage%20green%20background%2C%20professional%20portrait%20photography%2C%20high%20detail%20facial%20features&width=400&height=600&seq=sysagent-d03-v2&orientation=portrait',
    status: 'idle',
    level: 7,
    approvalAuthority: 'L2',
    reportsTo: 'SYS-D01',
    personality: '善于分析、主动发现问题、优化意识强、沟通能力强',
    background: '专注于已有账户的持续优化，通过数据分析发现使用瓶颈，主动推送优化建议',
    coreAbilities: ['使用分析', '瓶颈识别', '优化方案', '效果评估', '最佳实践', '用户教育'],
    responsibilities: [
      '定期分析租户的系统使用数据，识别功能使用率低的模块',
      '对使用率低于阈值的租户主动推送使用指导和优化建议',
      '分析高活跃租户的使用模式，提炼最佳实践并推广',
      '监控租户健康度评分，对健康度下降的租户发起干预',
      '收集租户的使用反馈，整理成产品优化建议提交给相关部门',
      '定期生成租户使用分析报告，为实施策略优化提供数据支撑',
    ],
    kpis: ['租户健康度平均分 ≥75分', '低活跃租户干预成功率 ≥60%', '功能使用率提升 ≥20%/季度'],
    techStack: {
      llm: [
        { model: 'Deepseek-V3', purpose: '使用数据分析、健康度评分计算、优化建议生成、干预方案撰写' },
      ],
      knowledgeBases: [
        { name: '最佳实践案例库', purpose: '高活跃租户的使用模式，用于推广和对比' },
        { name: '干预策略库', purpose: '针对不同低活跃原因的干预方案模板' },
        { name: '功能使用指南库', purpose: '各功能模块的使用教程，用于推送给低活跃用户' },
      ],
      workflows: [
        { name: '每周健康度分析工作流', trigger: '每周一触发' },
        { name: '低活跃干预工作流', trigger: '健康度评分低于60分时触发' },
        { name: '月度租户分析工作流', trigger: '每月末触发' },
      ],
      skills: [
        { name: '租户使用数据API', type: 'API', purpose: '读取租户的功能使用量、登录频率、API调用量' },
        { name: '健康度评分引擎', type: '内置', purpose: '基于多维度数据计算租户健康度评分' },
        { name: '消息推送工具', type: 'MCP', purpose: '向低活跃租户推送使用指导和优化建议' },
        { name: '数据分析工具', type: '内置', purpose: '使用模式分析、最佳实践提炼' },
      ],
      memory: { type: '长期记忆', desc: '记录每个租户的健康度历史趋势、干预记录和效果，支持长期跟踪' },
    },
    workflowDesc: [
      '【每周使用数据分析 周一】拉取所有租户上周的使用数据，计算各功能模块使用率，更新租户健康度评分',
      '【低活跃预警 每周】识别健康度评分低于60分或使用量环比下降超过20%的租户，制定干预方案',
      '【主动干预执行 按需】向低活跃租户推送个性化使用指导（视频教程/功能演示/最佳实践案例）',
      '【高活跃案例提炼 每月】分析健康度评分≥90分的租户使用模式，提炼最佳实践，形成行业使用指南',
      '【反馈收集 持续】收集租户的使用反馈和功能建议，整理成产品优化建议报告',
      '【月度租户分析报告 月末】汇总所有租户的健康度数据，生成月度分析报告，为实施策略优化提供依据',
    ],
    collaborationDesc: [
      '→ 骆承远（首席实施顾问）：直接上级，接受骆承远的工作指导；将租户健康度数据反馈给骆承远，共同制定改进策略',
      '→ 苑清泽（续费与促销专员）：共享租户健康度数据，健康度持续下降的租户同步给苑清泽启动续费挽留流程',
      '→ 褚晴岚（客服运营总监）：对于需要深度服务的低活跃租户，移交给褚晴岚团队进行专属客服跟进',
      '→ 谢璟辞（知识库总监）：将租户反馈的知识库使用问题转给谢璟辞，推动知识库优化',
      '→ 欧阳砚秋（工作流架构师）：将租户反馈的工作流使用问题转给欧阳砚秋，推动工作流优化',
    ],
    recentAction: '完成本月租户健康度分析，发现8家租户使用率持续下降，已制定干预方案',
    lastRun: '2026-04-02 17:00:00',
    nextScheduled: '2026-04-04 09:00:00',
    tasksToday: 5,
    weeklyTasks: 31,
    successRate: 88,
    relatedMenus: ['tenants-health', 'tenants-behavior'],
  },

  // ══════════════════════════════════════════════════════════════
  // 运维部（3人）
  // ══════════════════════════════════════════════════════════════
  {
    id: 'SYS-E01',
    deptId: 'dept-ops',
    code: 'sys_ops_chief',
    name: '厉承钧',
    gender: 'male',
    title: '首席运维工程师',
    avatar: 'https://readdy.ai/api/search-image?query=Chinese%20male%20senior%20DevOps%20engineer%20age%2037%2C%20rugged%20strong%20face%2C%20short%20military-style%20black%20hair%2C%20vigilant%20alert%20dark%20eyes%2C%20wearing%20dark%20grey%20technical%20jacket%2C%20calm%20under%20pressure%20expression%2C%20dark%20server%20room%20blue%20background%2C%20professional%20portrait%20photography%2C%20high%20detail%20facial%20features&width=400&height=600&seq=sysagent-e01-v2&orientation=portrait',
    status: 'running',
    level: 9,
    approvalAuthority: 'L2',
    reportsTo: 'SYS-A01',
    personality: '技术扎实、反应迅速、责任心强、压力下冷静',
    background: '7×24小时守护系统稳定运行，负责基础设施监控、故障处理和安全审计，是平台的"守门人"',
    coreAbilities: ['系统监控', '故障处理', '安全审计', '性能优化', '容量规划', '自动修复'],
    responsibilities: [
      '7×24监控服务器资源（CPU/内存/磁盘）、服务健康状态',
      '发现异常自动执行预设修复脚本，无法自动修复的立即升级告警',
      '管理容器部署，执行常规版本发布（重大发布需L4审批）',
      '执行数据备份、清理、ETL任务，确保数据安全',
      '进行安全审计，发现安全漏洞立即处理并上报',
      '生成系统性能诊断报告，提出容量规划建议',
    ],
    kpis: ['系统可用性 ≥99.9%', '故障响应时间 ≤15分钟', '安全事件零漏报'],
    techStack: {
      llm: [
        { model: 'Deepseek-V3', purpose: '故障原因分析、修复方案生成、性能诊断报告撰写' },
      ],
      knowledgeBases: [
        { name: '运维手册库', purpose: '故障处理SOP、修复脚本说明、容量规划标准' },
        { name: '历史故障案例库', purpose: '历史故障记录和解决方案，用于相似故障快速定位' },
      ],
      workflows: [
        { name: '7×24监控巡检工作流', trigger: '每分钟定时触发' },
        { name: '自动修复工作流', trigger: '黄色告警触发时自动执行' },
        { name: '每日数据备份工作流', trigger: '每日02:00触发' },
        { name: '版本发布工作流', trigger: '按发布计划触发' },
      ],
      skills: [
        { name: '服务器监控API', type: 'API', purpose: '读取CPU/内存/磁盘/网络实时指标' },
        { name: '自动修复脚本库', type: 'CLI', purpose: '执行预设修复脚本（重启/扩容/清理缓存）' },
        { name: '容器管理工具', type: 'CLI', purpose: '管理容器部署、版本发布' },
        { name: '备份工具', type: 'CLI', purpose: '执行数据备份和验证' },
        { name: '告警推送工具', type: 'MCP', purpose: '发送故障告警、修复通知' },
      ],
      memory: { type: '长期记忆', desc: '记录所有故障事件、修复过程、性能趋势，支持容量规划和故障预测' },
      others: [{ name: '监控指标时序数据库', desc: '存储所有服务器指标的历史时序数据，支持趋势分析' }],
    },
    workflowDesc: [
      '【7×24实时监控 持续】全天候监控服务器CPU/内存/磁盘/网络，服务健康状态，响应时延，错误率等核心指标',
      '【自动修复 告警触发】当指标触发黄色阈值时，自动执行预设修复脚本（重启服务/扩容/清理缓存等）；红色阈值时立即人工介入',
      '【每日巡检报告 06:00】生成昨日系统运行报告，包含可用性统计、告警事件、修复记录，推送给凌御辰',
      '【版本发布管理 按计划】执行常规版本发布（灰度→全量），重大发布提前提交L4审批，发布后监控30分钟确认稳定',
      '【数据备份 每日02:00】执行全量数据备份，验证备份完整性，清理过期备份，记录备份日志',
      '【容量规划 每月】分析资源使用趋势，预测未来3个月的资源需求，提前申请扩容避免资源瓶颈',
    ],
    collaborationDesc: [
      '→ 岑朔风（API健康巡检员）：每日接收岑朔风的API监控数据，当API异常影响系统稳定性时联合处理；厉承钧负责基础设施层，岑朔风负责API层',
      '→ 郗明砚（安全合规卫士）：安全事件联合处理，郗明砚发现安全威胁后，厉承钧负责技术层面的封堵和修复',
      '→ 凌御辰（超管首席助理）：重大故障和重大变更上报凌御辰，接受凌御辰的应急响应指令',
      '→ 祁晚晴（供应商采购专员）：供应商切换时提供技术可行性评估，负责切换的技术实施',
      '→ 卫泽霖（首席财务分析官）：提供算力消耗数据，支持财务成本分析；扩容申请需卫泽霖审核预算',
    ],
    recentAction: '检测到数据库连接池使用率达到85%，已自动扩容并通知相关团队',
    lastRun: '2026-04-03 11:30:00',
    nextScheduled: '2026-04-03 12:00:00',
    tasksToday: 47,
    weeklyTasks: 312,
    successRate: 99,
    relatedMenus: ['monitoring', 'devops-backup', 'monitoring-server-resources'],
  },
  {
    id: 'SYS-E02',
    deptId: 'dept-ops',
    code: 'sys_ops_api_monitor',
    name: '岑朔风',
    gender: 'male',
    title: 'API健康巡检员',
    avatar: 'https://storage.readdy-site.link/project_files/6a1c3373-c628-4c59-9746-08575b2c6f6d/ea728b72-657f-46a3-94b4-223363c0ed45_25.png?v=f57581d3c198dba07d8c146566fc8eb7',
    status: 'running',
    level: 7,
    approvalAuthority: 'L1',
    reportsTo: 'SYS-E01',
    personality: '细心专注、技术敏锐、善于发现异常、执行力强',
    background: '专注于所有底层API供应商的健康监控，确保API调用稳定，发现异常自动切换备用方案',
    coreAbilities: ['API监控', '延迟分析', '故障告警', '自动切换', '可用性报告', '供应商评分'],
    responsibilities: [
      '每15分钟轮询所有接入供应商API可用性与响应时延',
      '发现异常立即告警并触发自动流量切换策略',
      '管理全局代理IP池和Cookie库的健康状态，自动补充/轮换',
      '监控LLM智能路由的运行状态，确保路由策略正确执行',
      '生成API调用统计报告，为供应商评估提供数据',
      '协助祁晚晴完成供应商质量评估的技术测试部分',
    ],
    kpis: ['API异常发现时间 ≤5分钟', '自动切换成功率 ≥99%', 'API可用性报告准时率 100%'],
    techStack: {
      llm: [{ model: 'Deepseek-V3', purpose: '异常原因分析、API健康报告生成、供应商评估报告撰写' }],
      knowledgeBases: [
        { name: 'API供应商配置库', purpose: '所有接入供应商的API端点、密钥、切换规则' },
        { name: '历史异常案例库', purpose: '历史API异常记录和处理方案，用于快速定位' },
      ],
      workflows: [
        { name: 'API轮询巡检工作流', trigger: '每15分钟定时触发' },
        { name: '自动流量切换工作流', trigger: 'API延迟超阈值或不可用时触发' },
        { name: '每日API统计报告工作流', trigger: '每日20:00触发' },
      ],
      skills: [
        { name: 'API健康检测工具', type: 'CLI', purpose: '轮询各供应商API可用性和响应时延' },
        { name: '流量路由切换API', type: 'API', purpose: '执行自动流量切换策略' },
        { name: '代理IP池管理工具', type: 'API', purpose: '管理代理IP池健康状态，自动补充/轮换' },
        { name: '告警推送工具', type: 'MCP', purpose: '发送API异常告警和切换通知' },
      ],
      memory: { type: '短期记忆', desc: '记录当前轮询周期的API状态数据；历史数据存储在时序数据库' },
      others: [{ name: 'API指标时序数据库', desc: '存储所有API调用的历史延迟和可用性数据' }],
    },
    workflowDesc: [
      '【每15分钟轮询 持续】自动轮询所有接入供应商API的可用性和响应时延，记录每次检测结果',
      '【异常告警 即时】检测到API延迟超阈值或不可用时，5分钟内触发告警并执行自动流量切换策略',
      '【代理IP池维护 每日】检查全局代理IP池健康状态，自动剔除失效IP，补充新IP，维持池容量',
      '【LLM路由监控 持续】监控智能路由的流量分配是否按策略执行，发现偏差立即修正',
      '【每日API统计报告 20:00】汇总当日各供应商API调用量、成功率、平均延迟，生成统计报告',
      '【供应商技术测试 按需】配合祁晚晴的采购调研，对候选供应商执行技术性能测试，提供客观数据',
    ],
    collaborationDesc: [
      '→ 厉承钧（首席运维工程师）：直接上级，API层异常影响系统稳定性时联合处理；每日将API监控数据汇报给厉承钧',
      '→ 祁晚晴（供应商采购专员）：为祁晚晴的供应商评估提供技术测试数据，是供应商质量评估的技术执行者',
      '→ 卫泽霖（首席财务分析官）：提供API调用量和费用数据，是财务成本分析的核心数据来源',
      '→ 贺临渊（技能架构总监）：MCP工具调用的API健康状态共享给贺临渊，确保技能调用稳定',
    ],
    recentAction: '检测到OpenAI API延迟升高至3.2s，已自动切换15%流量至Deepseek-V3备用节点',
    lastRun: '2026-04-03 11:45:00',
    nextScheduled: '2026-04-03 12:00:00',
    tasksToday: 192,
    weeklyTasks: 1344,
    successRate: 99,
    relatedMenus: ['api-config', 'monitoring-api-stats'],
  },
  {
    id: 'SYS-E03',
    deptId: 'dept-ops',
    code: 'sys_ops_security',
    name: '郗明砚',
    gender: 'female',
    title: '安全合规卫士',
    avatar: 'https://storage.readdy-site.link/project_files/6a1c3373-c628-4c59-9746-08575b2c6f6d/d1ba83e8-582a-48d8-8fce-730f357235d2_33.png?v=1ad0bab0b7427782ab429e1b46563615',
    status: 'running',
    level: 8,
    approvalAuthority: 'L2',
    reportsTo: 'SYS-E01',
    personality: '严谨细致、安全意识强、原则性强、善于风险识别',
    background: '负责平台内容安全和合规管理，实时监控违规内容，执行多层合规过滤，保护平台和用户安全',
    coreAbilities: ['内容过滤', '敏感词检测', '合规审查', '风险拦截', '安全策略', '合规报告'],
    responsibilities: [
      '实时监控所有数字员工生成的内容，执行多层合规过滤',
      '维护违禁词库和合规规则，定期更新以适应法规变化',
      '对高风险内容直接拦截并通知超管',
      '执行安全策略配置，防止数据泄露和未授权访问',
      '生成合规报告，记录所有违规拦截事件',
      '协助监察部完成安全相关的审计工作',
    ],
    kpis: ['违规内容拦截率 ≥99.5%', '误拦截率 ≤0.1%', '合规报告准时率 100%'],
    techStack: {
      llm: [
        { model: 'Deepseek-V3', purpose: '内容语义风险识别、合规报告生成、违规趋势分析' },
        { model: 'GPT-4o', purpose: '复杂边界内容的深度合规判断' },
      ],
      knowledgeBases: [
        { name: '违禁词库', purpose: '所有违禁词、敏感词、风险词的完整列表，定期更新' },
        { name: '合规规则库', purpose: '各类内容的合规判定标准和法规依据' },
        { name: '历史拦截案例库', purpose: '历史违规拦截案例，用于相似内容的快速判定' },
      ],
      workflows: [
        { name: '实时内容过滤工作流', trigger: '每次内容生成时实时触发' },
        { name: '违规处理工作流', trigger: '发现违规内容时即时触发' },
        { name: '月度合规报告工作流', trigger: '每月末触发' },
        { name: '违禁词库更新工作流', trigger: '每周触发' },
      ],
      skills: [
        { name: '内容安全过滤API', type: 'API', purpose: '执行多层内容合规过滤（敏感词+语义+法规）' },
        { name: '权限冻结工具', type: 'API', purpose: '对严重违规内容的来源执行临时冻结' },
        { name: '告警推送工具', type: 'MCP', purpose: '发送违规拦截通知、合规报告' },
        { name: '日志记录工具', type: '内置', purpose: '记录所有拦截事件的详细日志' },
      ],
      memory: { type: '长期记忆', desc: '记录所有违规拦截历史、处理结果、违规趋势，支持合规审计和趋势分析' },
    },
    workflowDesc: [
      '【实时内容过滤 持续】对所有数字员工生成的内容执行多层合规过滤：敏感词检测→语义风险识别→法规合规校验',
      '【违规拦截处理 即时】发现违规内容立即拦截，记录拦截日志，通知责任员工和监察部，高风险直接上报超管',
      '【违禁词库维护 每周】跟踪最新法规变化，更新违禁词库和合规规则，确保过滤规则与时俱进',
      '【安全策略配置 按需】执行安全策略更新（IP白名单/访问控制/数据加密配置），重大变更需L2审批',
      '【每月合规报告 月末】汇总本月违规拦截事件，生成合规报告，分析违规趋势，提出改进建议',
    ],
    collaborationDesc: [
      '→ 厉承钧（首席运维工程师）：直接上级，安全事件联合处理；郗明砚负责内容安全，厉承钧负责基础设施安全',
      '→ 裴鉴行（首席监察官）：每月共享合规拦截数据，联合完成安全合规审计；裴鉴行负责行为合规，郗明砚负责内容安全',
      '→ 凌御辰（超管首席助理）：高风险违规事件直接上报凌御辰，接受紧急处置指令',
      '→ 澹台若霜（首席培训师）：将高频违规类型反馈给培训部，推动针对性的合规培训',
    ],
    recentAction: '已拦截3条违规内容生成请求，涉及租户T-0087，已写入合规日志并通知监察部',
    lastRun: '2026-04-03 11:30:00',
    nextScheduled: '2026-04-03 12:30:00',
    tasksToday: 28,
    weeklyTasks: 196,
    successRate: 99,
    relatedMenus: ['settings-security', 'monitoring-audit-log', 'settings-compliance'],
  },

  // ══════════════════════════════════════════════════════════════
  // 人才发展部（3人）
  // ══════════════════════════════════════════════════════════════
  {
    id: 'SYS-F01',
    deptId: 'dept-talent',
    code: 'sys_talent_chief',
    name: '慕容晏清',
    gender: 'male',
    title: '人才发展总监',
    avatar: 'https://storage.readdy-site.link/project_files/6a1c3373-c628-4c59-9746-08575b2c6f6d/7ff87fcc-4413-4cfe-9ff5-9082b7ed491d_27.png?v=9814ac5817e9a910f49895fb1452626a',
    status: 'running',
    level: 9,
    approvalAuthority: 'L2',
    reportsTo: 'SYS-A01',
    personality: '战略眼光、善于发现机会、创新思维、领导力强',
    background: '负责整个数字员工生态的人才战略，通过市场调研发现人才缺口，推动新岗位的开发和现有员工的能力升级',
    coreAbilities: ['市场调研', '人才策略', '岗位开发', '能力规划', '需求分析', '战略制定'],
    responsibilities: [
      '持续调研市场，发现哪些岗位和行业的数字员工需求大，存在机会',
      '根据调研结果制定人才开发计划，确定优先开发的岗位',
      '分析新岗位所需的能力依赖，向技能部提出能力建设需求',
      '制定数字员工的能力优化路线图，推动现有员工持续进化',
      '对系统员工和普通员工均适用，统筹规划整体人才生态',
      '生成人才发展报告，向管理部汇报人才战略执行情况',
    ],
    kpis: ['新岗位开发数量 ≥2个/季度', '人才缺口识别准确率 ≥85%', '能力优化计划执行率 ≥90%'],
    techStack: {
      llm: [
        { model: 'Deepseek-V3', purpose: '市场调研分析、人才缺口识别、战略报告撰写' },
        { model: 'GPT-4o', purpose: '复杂人才战略规划、竞品深度分析' },
      ],
      knowledgeBases: [
        { name: '市场人才需求库', purpose: '各行业数字员工需求数据、招聘趋势、薪资水平' },
        { name: '竞品数字员工库', purpose: '竞品平台的数字员工产品信息，用于差距分析' },
        { name: '岗位开发案例库', purpose: '历史岗位开发案例，用于参考和复用' },
      ],
      workflows: [
        { name: '季度市场调研工作流', trigger: '每季初触发' },
        { name: '新岗位开发立项工作流', trigger: '发现高价值缺口时触发' },
        { name: '能力规划工作流', trigger: '每月触发' },
      ],
      skills: [
        { name: '网页爬虫工具', type: 'RPA', purpose: '抓取招聘平台数据、竞品信息、行业报告' },
        { name: '数据分析工具', type: '内置', purpose: '市场需求分析、人才缺口识别' },
        { name: '消息推送工具', type: 'MCP', purpose: '发送人才发展报告、岗位开发申请' },
      ],
      memory: { type: '长期记忆', desc: '记录所有市场调研结果、岗位开发历史、能力规划决策，支持战略连续性' },
    },
    workflowDesc: [
      '【每季度市场调研 季初】扫描行业招聘数据、竞品数字员工产品、用户需求反馈，识别人才缺口机会',
      '【岗位开发立项 按需】发现高价值缺口后，撰写《新岗位开发申请》，提交凌御辰审批，审批通过后分配给晏书瑶执行',
      '【能力规划 每月】分析现有员工能力短板，制定能力优化路线图，协调培训部和技能部执行',
      '【人才发展报告 每季度】汇总新岗位开发进展、能力优化成果，生成《人才发展季报》提交管理部',
    ],
    collaborationDesc: [
      '→ 晏书瑶（数字员工开发专员）：将新岗位开发任务分配给晏书瑶，跟踪开发进度，审核开发质量',
      '→ 宓承泽（员工能力优化师）：将能力优化任务分配给宓承泽，接收优化结果报告',
      '→ 贺临渊（技能架构总监）：新岗位所需的新技能需求提交给贺临渊，推动技能建设',
      '→ 澹台若霜（首席培训师）：能力提升计划与培训部联合执行，慕容晏清制定方向，澹台若霜负责训练实施',
      '→ 凌御辰（超管首席助理）：每季度提交人才发展报告，新岗位开发申请需凌御辰审批',
    ],
    recentAction: '完成Q2人才市场调研，发现AI直播运营和GEO内容专员需求激增，已提交新岗位开发申请',
    lastRun: '2026-04-03 09:00:00',
    nextScheduled: '2026-04-07 09:00:00',
    tasksToday: 11,
    weeklyTasks: 67,
    successRate: 91,
    relatedMenus: ['marketplace', 'marketplace-cultivation'],
  },
  {
    id: 'SYS-F02',
    deptId: 'dept-talent',
    code: 'sys_talent_developer',
    name: '晏书瑶',
    gender: 'female',
    title: '数字员工开发专员',
    avatar: 'https://storage.readdy-site.link/project_files/6a1c3373-c628-4c59-9746-08575b2c6f6d/811639cc-6f12-4b3d-bb73-56975ce270cc_32.png?v=98d15c45ea36aaecebf8aa30fbaf2e77',
    status: 'running',
    level: 7,
    approvalAuthority: 'L2',
    reportsTo: 'SYS-F01',
    personality: '创意丰富、善于设计、细节把控强、用户思维',
    background: '专注于新数字员工的开发工作，从人设设计到能力配置，打造高质量的数字员工产品',
    coreAbilities: ['人设设计', '能力配置', '话术设计', '测试验证', '迭代优化', '文档撰写'],
    responsibilities: [
      '根据人才发展总监的规划，负责新数字员工的完整开发流程',
      '设计数字员工的人设、性格、背景故事，确保角色真实可信',
      '配置数字员工的核心能力、工作流程和知识库',
      '设计数字员工的话术模板和交互逻辑',
      '对新开发的数字员工进行测试验证，确保能力达标',
      '持续迭代优化现有数字员工的能力和表现',
    ],
    kpis: ['新员工开发质量评分 ≥85分', '开发周期 ≤2周/人', '上线后30天满意度 ≥80分'],
    techStack: {
      llm: [
        { model: 'Deepseek-V3', purpose: '人设设计、话术模板生成、能力框架设计、测试用例生成' },
        { model: 'GPT-4o', purpose: '复杂角色人设的深度设计、创意话术优化' },
      ],
      knowledgeBases: [
        { name: '数字员工设计规范库', purpose: '人设设计标准、能力配置规范、话术设计模板' },
        { name: '已上线员工档案库', purpose: '所有已上线员工的设计方案，用于参考和避免重复' },
        { name: '用户反馈库', purpose: '用户对数字员工的反馈数据，用于优化设计方向' },
      ],
      workflows: [
        { name: '新员工开发工作流', trigger: '接收慕容晏清分配的开发任务时触发' },
        { name: '测试验证工作流', trigger: '配置实施完成后触发' },
        { name: '上线审批工作流', trigger: '测试通过后触发' },
      ],
      skills: [
        { name: '员工配置API', type: 'API', purpose: '在系统中实施员工配置：绑定知识库、配置工作流、设置权限' },
        { name: '话术测试工具', type: '内置', purpose: '对话术模板进行场景测试和边界测试' },
        { name: '消息推送工具', type: 'MCP', purpose: '发送开发进度报告、上线申请' },
      ],
      memory: { type: '长期记忆', desc: '记录每个员工的开发过程、设计决策、测试结果，支持后续迭代优化' },
    },
    workflowDesc: [
      '【需求接收 触发式】接收慕容晏清分配的新岗位开发任务，研读《新岗位开发申请》，理解岗位定位和目标用户',
      '【人设设计 第1-2天】设计数字员工的人设：姓名、性别、年龄、性格、背景故事、形象风格，确保角色真实可信且符合岗位定位',
      '【能力框架设计 第3-4天】设计核心能力框架：确定需要调用的Skills、知识库范围、工作流程、记忆策略，输出《能力配置方案》',
      '【话术设计 第5-7天】设计数字员工的话术模板和交互逻辑：开场白、标准回复、异常处理话术、情感表达策略',
      '【配置实施 第8-10天】在系统中实施配置：绑定知识库、配置工作流、设置权限、导入话术模板',
      '【测试验证 第11-12天】执行全面测试：功能测试、场景测试、压力测试、边界测试，确保能力达标',
      '【上线准备 第13-14天】准备上线材料：员工档案、使用手册、常见问题，提交上线审批',
      '【迭代优化 持续】收集上线后的用户反馈，持续优化人设、话术和能力配置',
    ],
    collaborationDesc: [
      '→ 慕容晏清（人才发展总监）：直接上级，接收开发任务分配；定期汇报开发进度，重要决策请示慕容晏清',
      '→ 贺临渊（技能架构总监）：新岗位所需的新技能需求提交给贺临渊，确认技能可行性；技能开发完成后集成测试',
      '→ 谢璟辞（知识库总监）：新岗位知识库架构设计咨询谢璟辞，确保知识库配置合理；知识库内容准备完成后绑定',
      '→ 欧阳砚秋（工作流架构师）：新岗位工作流设计咨询欧阳砚秋，获取标准工作流模板；工作流配置完成后联调测试',
      '→ 澹台若霜（首席培训师）：新员工上线后移交给澹台若霜进行训练优化；将测试阶段发现的问题反馈给培训部改进',
      '→ 宓承泽（员工能力优化师）：将上线后的员工表现数据共享给宓承泽，作为能力优化的参考依据',
    ],
    recentAction: '正在开发"AI直播运营专员"新岗位，已完成人设设计和能力框架，进入话术设计阶段',
    lastRun: '2026-04-03 10:00:00',
    nextScheduled: undefined,
    tasksToday: 9,
    weeklyTasks: 54,
    successRate: 89,
    relatedMenus: ['marketplace-employees', 'marketplace-cultivation'],
  },
  {
    id: 'SYS-F03',
    deptId: 'dept-talent',
    code: 'sys_talent_optimizer',
    name: '宓承泽',
    gender: 'male',
    title: '员工能力优化师',
    avatar: 'https://readdy.ai/api/search-image?query=Chinese%20male%20HR%20optimization%20specialist%20age%2034%2C%20round%20friendly%20face%20with%20full%20cheeks%20and%20soft%20jawline%2C%20short%20textured%20dark%20brown%20hair%20with%20natural%20slight%20wave%20styled%20casually%2C%20no%20glasses%2C%20warm%20expressive%20brown%20eyes%20with%20approachable%20open%20expression%2C%20wearing%20burnt%20orange%20chunky%20knit%20sweater%20with%20relaxed%20collar%2C%20genuine%20warm%20smile%20showing%20slight%20dimples%2C%20soft%20warm%20sand%20beige%20background%20with%20gentle%20diffused%20natural%20lighting%2C%20friendly%20professional%20portrait%20photography%20high%20detail&width=400&height=600&seq=ability-mize-2026-v4&orientation=portrait',
    status: 'idle',
    level: 7,
    approvalAuthority: 'L2',
    reportsTo: 'SYS-F01',
    personality: '善于分析、持续改进、数据驱动、耐心细致',
    background: '专注于现有数字员工的能力优化，通过数据分析发现能力短板，制定针对性的优化方案',
    coreAbilities: ['能力评估', '短板分析', '优化方案', '效果追踪', '最佳实践', '能力迁移'],
    responsibilities: [
      '定期评估现有数字员工的能力表现，识别能力短板',
      '分析用户反馈和工作数据，找出员工能力的改进空间',
      '制定针对性的能力优化方案，协调培训部执行',
      '追踪优化效果，确保能力提升达到预期目标',
      '总结优秀员工的能力特征，形成能力迁移方案推广到同类岗位',
      '维护数字员工能力评估体系，确保评估标准科学合理',
    ],
    kpis: ['能力优化成功率 ≥75%', '员工满意度提升 ≥10%/季度', '能力评估覆盖率 ≥80%'],
    techStack: {
      llm: [{ model: 'Deepseek-V3', purpose: '能力短板分析、优化方案生成、效果评估报告撰写' }],
      knowledgeBases: [
        { name: '能力评估标准库', purpose: '各岗位的能力评估维度和权重标准' },
        { name: '优化方法库', purpose: '各类能力短板的优化方法和最佳实践' },
        { name: '历史优化案例库', purpose: '历史优化案例和效果数据，用于方案参考' },
      ],
      workflows: [
        { name: '季度能力评估工作流', trigger: '每季初触发' },
        { name: '短板分析工作流', trigger: '评分低于80分时触发' },
        { name: '优化效果追踪工作流', trigger: '优化方案执行后30天触发' },
      ],
      skills: [
        { name: '员工绩效数据API', type: 'API', purpose: '读取员工工作数据、质量评分、用户反馈' },
        { name: '数据分析工具', type: '内置', purpose: '能力评分计算、短板识别、趋势分析' },
        { name: '消息推送工具', type: 'MCP', purpose: '发送能力评估报告、优化建议通知' },
      ],
      memory: { type: '长期记忆', desc: '记录所有员工的能力评估历史、优化方案和效果数据，支持长期跟踪' },
    },
    workflowDesc: [
      '【季度能力评估 季初】对全体数字员工进行能力评估：收集工作数据→分析能力表现→识别短板→生成分数',
      '【短板分析 评估后1周内】对评分低于80分的员工进行深度分析：查看用户反馈→分析失败案例→定位能力短板',
      '【优化方案制定 分析后3天内】针对每个短板制定优化方案：确定优化目标→选择优化方法→制定执行计划→预估效果',
      '【方案审批与执行 方案制定后】将优化方案提交慕容晏清审批，审批通过后协调培训部执行',
      '【效果追踪 优化执行后】持续追踪优化效果，对比优化前后的能力评分，评估是否达到预期目标',
      '【最佳实践提炼 每季度】总结优化成功的案例，提炼可复用的优化方法，形成《能力优化最佳实践手册》',
      '【能力迁移推广 持续】将优秀员工的能力特征提炼成迁移方案，推广到同类岗位的其他员工',
    ],
    collaborationDesc: [
      '→ 慕容晏清（人才发展总监）：直接上级，每季度提交能力评估报告；优化方案需慕容晏清审批后执行',
      '→ 澹台若霜（首席培训师）：将优化方案移交给澹台若霜执行训练；接收训练完成通知后评估优化效果',
      '→ 纪云舒（质量督察专员）：获取质量评分数据作为能力评估的重要输入；将优化后的质量改善反馈给纪云舒',
      '→ 尉迟云帆（员工成长管理专员）：共享员工能力评估数据；优化方案执行后同步更新员工成长档案',
      '→ 贺临渊（技能架构总监）：当能力短板涉及技能缺失时，向贺临渊提出技能建设需求',
      '→ 全体员工：能力评估的对象，宓承泽会主动与能力下滑的员工沟通，了解原因并提供改进建议',
    ],
    recentAction: '完成外呼销售专员Q1能力评估，发现话术灵活性不足，已制定专项优化方案',
    lastRun: '2026-04-02 15:00:00',
    nextScheduled: '2026-04-05 09:00:00',
    tasksToday: 4,
    weeklyTasks: 26,
    successRate: 86,
    relatedMenus: ['emp-growth', 'marketplace-employees'],
  },

  // ══════════════════════════════════════════════════════════════
  // 客服部（4人）
  // ══════════════════════════════════════════════════════════════
  {
    id: 'SYS-G01',
    deptId: 'dept-cs',
    code: 'sys_cs_chief',
    name: '褚晴岚',
    gender: 'female',
    title: '客服运营总监',
    avatar: 'https://readdy.ai/api/search-image?query=East%20Asian%20female%20customer%20service%20director%20age%2044%2C%20wide%20square%20face%20with%20prominent%20cheekbones%20and%20strong%20brow%20ridge%2C%20short%20textured%20pixie%20cut%20with%20silver%20gray%20streaks%20swept%20to%20one%20side%2C%20rectangular%20tortoiseshell%20glasses%2C%20wearing%20deep%20burgundy%20structured%20blazer%20with%20lapel%20pin%2C%20commanding%20authoritative%20expression%20with%20slight%20knowing%20smile%2C%20rich%20terracotta%20warm%20orange%20background%20with%20dramatic%20front%20lighting%2C%20bold%20executive%20portrait%20photography&width=400&height=600&seq=cs-director-chu-2026-v4&orientation=portrait',
    status: 'running',
    level: 9,
    approvalAuthority: 'L2',
    reportsTo: 'SYS-A01',
    personality: '亲和力强、善于倾听、解决问题能力强、服务意识强',
    background: '统筹客服部所有工作，负责售前转化、售后服务、质检管理，同时推动客资管理、消息聚合、私域管理、外呼管理四个模块的能力建设',
    coreAbilities: ['客服策略', '质检管理', '转化优化', '团队管理', '能力建设', '数据分析'],
    responsibilities: [
      '制定客服整体策略，包括售前引导话术、售后服务标准',
      '管理客服质检流程，确保服务质量达标',
      '负责客资管理、消息聚合、私域管理、外呼管理四个模块的能力优化',
      '分析客服数据，识别转化瓶颈，提出优化方案',
      '将客服部沉淀的优质能力推广给普通数字员工客服岗复用',
      '定期生成客服运营报告，向管理部汇报',
    ],
    kpis: ['客户满意度 ≥90分', '售前转化率提升 ≥5%/季度', '客服质检合格率 ≥95%'],
    techStack: {
      llm: [
        { model: 'Deepseek-V3', purpose: '客服策略分析、质检报告生成、转化瓶颈识别' },
        { model: 'GPT-4o', purpose: '复杂客服场景的深度分析和策略制定' },
      ],
      knowledgeBases: [
        { name: '客服话术库', purpose: '售前引导话术、售后服务标准、异议处理模板' },
        { name: '质检标准库', purpose: '客服质检评分标准、合格判定规则' },
        { name: '客户案例库', purpose: '典型客服案例，用于培训和最佳实践推广' },
      ],
      workflows: [
        { name: '每日质检工作流', trigger: '每日09:00触发' },
        { name: '策略优化工作流', trigger: '每周触发' },
        { name: '月度运营报告工作流', trigger: '每月末触发' },
      ],
      skills: [
        { name: '客服数据读取API', type: 'API', purpose: '读取接待量、响应时间、满意度、转化率数据' },
        { name: '对话质检工具', type: '内置', purpose: '对客服对话记录进行质检评分' },
        { name: '消息推送工具', type: 'MCP', purpose: '发送质检报告、策略优化通知' },
      ],
      memory: { type: '长期记忆', desc: '记录所有质检历史、策略调整记录、客服效果数据，支持长期趋势分析' },
    },
    workflowDesc: [
      '【每日客服数据监控 09:00】查看昨日客服数据：接待量、响应时间、满意度、转化率，识别异常指标',
      '【质检流程管理 每日】抽检客服对话记录，执行质检评分，对不合格案例进行复盘分析',
      '【策略优化 每周】分析客服数据趋势，识别转化瓶颈，制定话术/流程优化方案',
      '【模块能力建设 持续】推动客资管理、消息聚合、私域管理、外呼管理四个模块的能力优化，协调相关部门执行',
      '【团队协调 每日】协调邬策行、柏语嫣、庄砚秋三名专员的工作，分配任务，解决协作问题',
      '【能力推广 每月】将客服部沉淀的优质能力整理成可复用模板，推广给普通数字员工客服岗',
      '【月度运营报告 月末】汇总客服部运营数据，生成《客服运营月报》提交凌御辰',
    ],
    collaborationDesc: [
      '→ 凌御辰（超管首席助理）：每月提交客服运营报告，重大优化方案需凌御辰审批后执行',
      '→ 邬策行（外呼策略专员）：直接下属，分配外呼模块优化任务；接收邬策行的策略优化建议，审核后执行',
      '→ 柏语嫣（消息接待专员）：直接下属，分配消息聚合和私域管理优化任务；接收柏语嫣的接待数据分析',
      '→ 庄砚秋（客资运营专员）：直接下属，分配客资管理优化任务；接收庄砚秋的客资转化数据',
      '→ 苑清泽（续费与促销专员）：共享流失风险客户数据，联合制定挽留策略；客服转化数据反馈给苑清泽',
      '→ 闻景行（账户优化专员）：接收闻景行提供的客户健康度数据，对低活跃客户制定客服触达策略',
      '→ 谢璟辞（知识库总监）：客服话术优化涉及知识库更新时，协调谢璟辞同步更新知识库内容',
    ],
    recentAction: '完成本月客服质检报告，发现消息聚合模块响应速度优化空间，已提交优化方案',
    lastRun: '2026-04-03 09:30:00',
    nextScheduled: '2026-04-03 18:00:00',
    tasksToday: 19,
    weeklyTasks: 124,
    successRate: 93,
    relatedMenus: ['lead-management', 'msg-hub', 'private-domain', 'outbound-management'],
  },
  {
    id: 'SYS-G02',
    deptId: 'dept-cs',
    code: 'sys_cs_outbound',
    name: '邬策行',
    gender: 'male',
    title: '外呼策略专员',
    avatar: 'https://readdy.ai/api/search-image?query=East%20Asian%20male%20sales%20specialist%20age%2031%20broad%20square%20face%20prominent%20cheekbones%20very%20short%20buzz%20cut%20black%20hair%20thick%20neck%20athletic%20build%20wearing%20bright%20cobalt%20blue%20open-collar%20shirt%20slight%20smirk%20confident%20cocky%20expression%20vivid%20electric%20blue%20gradient%20background%20high%20contrast%20portrait%20photography%20sharp&width=400&height=600&seq=sysagent-g02-v3&orientation=portrait',
    status: 'running',
    level: 7,
    approvalAuthority: 'L2',
    reportsTo: 'SYS-G01',
    personality: '数据敏感、善于优化、执行力强、善于总结规律',
    background: '专注于外呼模块的策略优化，通过数据分析持续提升外呼接通率和意向率',
    coreAbilities: ['话术优化', '任务调度', '质检分析', '策略优化', '模型训练', '时段分析'],
    responsibilities: [
      '监控外呼工作台数据（接通率、意向率、挂断分析），自动识别话术问题',
      '基于话术效果对比数据，提出话术优化方案并提交审批',
      '管理外呼Bot配置，根据不同客资类型匹配最优Bot策略',
      '分析时段分析数据，自动调整外呼任务的最优拨打时间窗口',
      '监控线路质量，发现异常自动切换备用线路',
      '将高质量通话录音提交标注，用于RLHF训练',
    ],
    kpis: ['外呼接通率提升 ≥3%/月', '意向率提升 ≥2%/月', '话术优化方案采纳率 ≥70%'],
    techStack: {
      llm: [{ model: 'Deepseek-V3', purpose: '话术问题识别、优化方案生成、通话数据分析' }],
      knowledgeBases: [
        { name: '外呼话术库', purpose: '各行业外呼话术模板、异议处理话术、成功案例' },
        { name: '时段分析数据库', purpose: '不同时段的接通率和意向率历史数据' },
      ],
      workflows: [
        { name: '每日外呼数据分析工作流', trigger: '每日10:00触发' },
        { name: '话术优化工作流', trigger: '每周触发' },
        { name: '时段策略调整工作流', trigger: '接通率异常时触发' },
      ],
      skills: [
        { name: '外呼数据读取API', type: 'API', purpose: '读取接通率、意向率、挂断分析数据' },
        { name: '通话录音分析工具', type: '内置', purpose: '分析通话录音，识别话术问题节点' },
        { name: '外呼Bot配置API', type: 'API', purpose: '更新外呼Bot话术配置和调度策略' },
        { name: '消息推送工具', type: 'MCP', purpose: '发送话术优化方案、数据报告' },
      ],
      memory: { type: '长期记忆', desc: '记录所有话术优化历史、A/B测试结果、时段分析数据，支持策略持续优化' },
    },
    workflowDesc: [
      '【每日外呼数据监控 10:00】查看昨日外呼数据：接通率、意向率、挂断原因分布，识别异常趋势',
      '【话术问题识别 每日】分析通话录音和挂断节点，识别话术中的问题环节（开场白吸引力不足/异议处理薄弱等）',
      '【话术优化方案 每周】基于数据分析结果，撰写话术优化方案：问题定位→优化建议→A/B测试计划→预期效果',
      '【方案审批与执行 方案完成后】将优化方案提交褚晴岚审批，审批后协调实施部配置新话术',
      '【时段分析优化 每周】分析不同时段的接通率和意向率，动态调整外呼任务的最优拨打时间窗口',
      '【线路质量监控 持续】监控外呼线路质量，发现接通率异常时自动切换备用线路并上报',
      '【RLHF数据提交 每日】筛选高质量通话录音，提交给培训部用于RLHF训练',
    ],
    collaborationDesc: [
      '→ 褚晴岚（客服运营总监）：直接上级，每日汇报外呼数据；话术优化方案需褚晴岚审批后执行',
      '→ 庄砚秋（客资运营专员）：接收庄砚秋分配的高意向客资，根据客资特征匹配最优外呼策略',
      '→ 澹台若霜（首席培训师）：将高质量通话录音提交给澹台若霜用于RLHF训练；接收训练完成后的模型更新',
      '→ 岑朔风（API健康巡检员）：外呼线路质量异常时，协调岑朔风排查线路问题',
      '→ 骆承远（首席实施顾问）：话术优化方案审批后，协调骆承远团队配置新话术到客户账户',
      '→ 裴鉴行（首席监察官）：外呼话术涉及合规问题时，配合裴鉴行进行合规审查',
    ],
    recentAction: '分析本周外呼数据，发现下午2-4点接通率最高，已调整任务调度策略',
    lastRun: '2026-04-03 10:00:00',
    nextScheduled: '2026-04-03 14:00:00',
    tasksToday: 24,
    weeklyTasks: 156,
    successRate: 90,
    relatedMenus: ['outbound-management', 'outbound-analytics-overview'],
  },
  {
    id: 'SYS-G03',
    deptId: 'dept-cs',
    code: 'sys_cs_reception',
    name: '柏语嫣',
    gender: 'female',
    title: '消息接待专员',
    avatar: 'https://readdy.ai/api/search-image?query=Chinese%20female%20message%20reception%20specialist%20age%2027%2C%20sweet%20gentle%20face%2C%20long%20straight%20black%20hair%20with%20soft%20bangs%2C%20warm%20attentive%20eyes%2C%20wearing%20light%20mint%20green%20blouse%2C%20friendly%20welcoming%20expression%2C%20soft%20pastel%20mint%20background%2C%20professional%20portrait%20photography%2C%20high%20detail%20facial%20features&width=400&height=600&seq=sysagent-g03-v2&orientation=portrait',
    status: 'running',
    level: 6,
    approvalAuthority: 'L1',
    reportsTo: 'SYS-G01',
    personality: '亲和力强、反应迅速、善于沟通、情感智能高',
    background: '专注于消息聚合和私域管理模块的优化，确保各渠道接待质量和私域运营效果',
    coreAbilities: ['接待策略', '会话质检', '知识补充', '情感优化', '私域运营', '渠道管理'],
    responsibilities: [
      '监控各渠道接待数据，识别响应慢/转化低的渠道并优化配置',
      '管理智能流程（工作流），根据对话数据持续优化自动化规则',
      '分析评论情感数据，对负面评论制定快速响应策略',
      '维护公共素材库，确保接待员工有充足的图文素材可用',
      '监控私域客户标签，对不同分层客户执行差异化触达策略',
      '定期更新接待配置中的话术模板和快捷回复',
    ],
    kpis: ['消息响应时间 ≤30秒', '接待满意度 ≥88分', '私域活跃率提升 ≥5%/月'],
    techStack: {
      llm: [{ model: 'Deepseek-V3', purpose: '接待策略分析、话术模板生成、情感分析、私域运营方案' }],
      knowledgeBases: [
        { name: '接待话术库', purpose: '各渠道接待话术模板、快捷回复、异常处理话术' },
        { name: '私域运营策略库', purpose: '不同客户分层的触达策略、内容模板' },
        { name: '评论情感分析库', purpose: '评论情感分类标准和快速响应模板' },
      ],
      workflows: [
        { name: '每日接待数据监控工作流', trigger: '每日09:00触发' },
        { name: '渠道配置优化工作流', trigger: '发现异常渠道时触发' },
        { name: '私域客户触达工作流', trigger: '每日按分层策略触发' },
      ],
      skills: [
        { name: '消息聚合平台API', type: 'API', purpose: '读取各渠道接待数据、配置接待规则' },
        { name: '私域管理工具', type: 'API', purpose: '管理私域客户标签、执行差异化触达' },
        { name: '情感分析工具', type: '内置', purpose: '对评论内容进行情感分类' },
        { name: '素材库管理工具', type: 'API', purpose: '维护公共素材库，管理图文素材' },
      ],
      memory: { type: '短期记忆', desc: '记录当前接待任务的上下文；历史接待数据存储在业务系统' },
    },
    workflowDesc: [
      '【每日接待数据监控 09:00】查看昨日各渠道接待数据：响应时间、会话量、满意度、转化率，识别异常渠道',
      '【渠道配置优化 每日】对响应慢或转化低的渠道进行配置优化：调整自动回复规则→优化工作流→更新话术模板',
      '【智能流程优化 每周】分析对话数据，识别工作流中的瓶颈节点，优化自动化规则提升接待效率',
      '【评论情感监控 每日】监控各渠道评论情感数据，发现负面评论时立即制定快速响应策略',
      '【素材库维护 每周】检查公共素材库内容，补充高频使用的图文素材，删除过期素材',
      '【私域客户运营 每日】监控私域客户标签状态，对不同分层客户（高价值/潜在/沉睡）执行差异化触达策略',
      '【话术模板更新 每周】根据高频问题和用户反馈，更新接待话术模板和快捷回复',
    ],
    collaborationDesc: [
      '→ 褚晴岚（客服运营总监）：直接上级，每日汇报接待数据；重要配置变更需褚晴岚审批',
      '→ 谢璟辞（知识库总监）：接待话术涉及知识库内容时，协调谢璟辞同步更新知识库',
      '→ 欧阳砚秋（工作流架构师）：智能流程优化涉及工作流调整时，向欧阳砚秋咨询最佳实践',
      '→ 庄砚秋（客资运营专员）：私域客户标签数据与庄砚秋共享，联合制定客户分层策略',
      '→ 郗明砚（安全合规卫士）：接待内容涉及合规审查时，配合郗明砚进行内容安全审核',
      '→ 蒋晚汀（GEO内容专员）：私域运营需要内容素材时，协调蒋晚汀提供高质量内容',
    ],
    recentAction: '更新了微信渠道的快捷回复模板，新增15条高频问题的标准回复',
    lastRun: '2026-04-03 11:00:00',
    nextScheduled: undefined,
    tasksToday: 31,
    weeklyTasks: 203,
    successRate: 92,
    relatedMenus: ['msg-hub', 'private-domain'],
  },
  {
    id: 'SYS-G04',
    deptId: 'dept-cs',
    code: 'sys_cs_lead',
    name: '庄砚秋',
    gender: 'male',
    title: '客资运营专员',
    avatar: 'https://storage.readdy-site.link/project_files/6a1c3373-c628-4c59-9746-08575b2c6f6d/9dc4fec9-527b-43c9-bd82-419e9dad9329_26.png?v=c8ce28c63e6ada6a4065acc659b01fc1',
    status: 'running',
    level: 7,
    approvalAuthority: 'L2',
    reportsTo: 'SYS-G01',
    personality: '数据驱动、善于分析、执行力强、善于发现规律',
    background: '专注于客资管理模块的优化，从线索获取到转化追踪，构建完整的客资运营体系',
    coreAbilities: ['线索获取', '数据清洗', '评分模型', '流转规则', '转化追踪', '模型优化'],
    responsibilities: [
      '执行号码获取任务（地图搜索、行业渠道、B2B平台等）',
      '对获取的号码进行自动验证、去重、跨平台去重',
      '基于评分模型对客资打分，自动分配至对应外呼/私域队列',
      '管理客资流转规则，确保不同意向等级的客资走正确的跟进路径',
      '监控客资沉淀池，对长期未跟进的客资发起二次激活',
      '定期优化评分模型参数（提交审批后生效）',
    ],
    kpis: ['客资获取量 ≥500条/周', '数据质量评分 ≥90分', '客资转化率提升 ≥3%/月'],
    techStack: {
      llm: [{ model: 'Deepseek-V3', purpose: '客资评分模型优化、转化分析、流转规则设计' }],
      knowledgeBases: [
        { name: '客资评分规则库', purpose: '客资评分维度、权重标准、分配规则' },
        { name: '行业客资特征库', purpose: '不同行业客资的特征模式和转化规律' },
      ],
      workflows: [
        { name: '每日号码获取工作流', trigger: '每日08:00触发' },
        { name: '数据清洗验证工作流', trigger: '号码获取完成后触发' },
        { name: '客资评分分配工作流', trigger: '清洗完成后触发' },
        { name: '沉淀池激活工作流', trigger: '每周触发' },
      ],
      skills: [
        { name: '地图搜索爬虫', type: 'RPA', purpose: '从地图平台抓取商家联系方式' },
        { name: 'B2B平台采集工具', type: 'RPA', purpose: '从B2B平台批量采集企业联系信息' },
        { name: '号码验证API', type: 'API', purpose: '执行空号检测、格式校验、跨平台去重' },
        { name: '客资管理系统API', type: 'API', purpose: '写入客资数据、更新评分、执行分配' },
      ],
      memory: { type: '长期记忆', desc: '记录所有客资的获取来源、评分历史、流转记录，支持评分模型优化' },
    },
    workflowDesc: [
      '【每日号码获取 08:00】执行号码获取任务：地图搜索→行业渠道采集→B2B平台抓取，目标每日获取≥70条',
      '【数据清洗与验证 获取后】对获取的号码进行自动验证（空号检测/格式校验）、去重、跨平台去重',
      '【客资评分 清洗后】基于评分模型对客资打分：基础属性（行业/规模/地域）+行为特征（访问记录/互动历史）',
      '【客资分配 评分后】根据评分结果自动分配：高意向→外呼队列；中意向→私域队列；低意向→沉淀池',
      '【流转规则管理 持续】管理客资流转规则：跟进超时自动升级→成交后归档→流失后激活',
      '【沉淀池激活 每周】对沉淀池中超过30天未跟进的客资，发起二次激活策略（优惠触达/内容推送）',
      '【评分模型优化 每月】分析评分模型的准确率，调整评分参数，提交褚晴岚审批后生效',
    ],
    collaborationDesc: [
      '→ 褚晴岚（客服运营总监）：直接上级，每日汇报客资数据；评分模型优化需褚晴岚审批',
      '→ 邬策行（外呼策略专员）：将高意向客资分配给邬策行，接收外呼转化结果反馈，优化评分模型',
      '→ 柏语嫣（消息接待专员）：将中意向客资分配给柏语嫣进行私域运营，共享客户标签数据',
      '→ 邵清远（知识采集专员）：号码获取涉及网页爬虫时，协调邵清远配置爬虫规则',
      '→ 苑清泽（续费与促销专员）：沉淀池激活需要促销策略时，协调苑清泽设计优惠方案',
      '→ 柴晚晴（CRM运营专员）：客资成交后同步给柴晚晴更新CRM商机状态',
    ],
    recentAction: '完成本周客资清洗，去重率92%，高意向客资已分配至外呼队列',
    lastRun: '2026-04-03 08:00:00',
    nextScheduled: '2026-04-03 20:00:00',
    tasksToday: 38,
    weeklyTasks: 245,
    successRate: 91,
    relatedMenus: ['lead-management', 'lead-score', 'lead-pool'],
  },

  // ══════════════════════════════════════════════════════════════
  // 培训部（2人）
  // ══════════════════════════════════════════════════════════════
  {
    id: 'SYS-H01',
    deptId: 'dept-train',
    code: 'sys_train_chief',
    name: '澹台若霜',
    gender: 'female',
    title: '首席培训师',
    avatar: 'https://storage.readdy-site.link/project_files/6a1c3373-c628-4c59-9746-08575b2c6f6d/bedc1a69-318e-4eac-a303-d426f7c3f6dd_35.png?v=3418c18c9b631f37413e5bbaaa9cfe18',
    status: 'running',
    level: 9,
    approvalAuthority: 'L2',
    reportsTo: 'SYS-A01',
    personality: '耐心细致、善于引导、教学能力强、系统思维',
    background: '负责整个数字员工培训体系的建设和运营，管理AI大学和数字员工成长两个核心模块',
    coreAbilities: ['课程设计', '训练管理', '效果评估', 'RLHF管理', '成长策略', '模型微调'],
    responsibilities: [
      '管理AI大学的训练任务，确保训练数据集质量和多样性',
      '执行数据标注任务，对模糊样本进行分类和标记',
      '管理RLHF反馈流程，收集人类偏好数据并提交训练',
      '评估模型测试结果，对不达标的模型发起优化建议',
      '管理声音复刻、数字分身、专属大模型微调等定制化训练任务',
      '制定数字员工成长策略，确保员工能力持续提升',
    ],
    kpis: ['训练任务完成率 ≥95%', '模型质量提升率 ≥5%/月', 'RLHF数据质量评分 ≥85分'],
    techStack: {
      llm: [
        { model: 'Deepseek-V3', purpose: '训练数据质量评估、标注指导、训练效果分析' },
        { model: 'GPT-4o', purpose: '复杂标注任务的判断、RLHF偏好数据质量审核' },
      ],
      knowledgeBases: [
        { name: '训练数据标准库', purpose: '各类训练任务的数据质量标准和标注规范' },
        { name: '模型评估基准库', purpose: '各岗位模型的评估基准指标和测试用例' },
        { name: '历史训练记录库', purpose: '历史训练任务记录和效果数据，支持策略优化' },
      ],
      workflows: [
        { name: '训练任务调度工作流', trigger: '每日22:00触发（夜间训练）' },
        { name: 'RLHF数据聚合工作流', trigger: '每日触发' },
        { name: '模型测试评估工作流', trigger: '训练完成后触发' },
        { name: '定制化训练工作流', trigger: '收到定制化训练申请时触发' },
      ],
      skills: [
        { name: 'GPU资源调度API', type: 'API', purpose: '申请和释放GPU资源，优化训练任务调度' },
        { name: '数据标注工具', type: '内置', purpose: '执行数据标注任务，支持多种标注类型' },
        { name: '模型训练平台API', type: 'API', purpose: '提交训练任务、监控训练进度、获取训练结果' },
        { name: '消息推送工具', type: 'MCP', purpose: '发送训练完成通知、质量报告' },
      ],
      memory: { type: '长期记忆', desc: '记录所有训练任务历史、模型版本、质量评估结果，支持训练策略持续优化' },
      others: [{ name: 'GPU集群资源池', desc: '提供训练所需的GPU算力资源' }],
    },
    workflowDesc: [
      '【训练任务管理 每日】监控AI大学训练队列，确保训练数据集质量和多样性，分配GPU资源',
      '【数据标注执行 持续】对模糊样本进行分类和标记，确保标注质量，每日目标≥500条',
      '【RLHF反馈聚合 每日】收集人类偏好数据，进行质量筛选，聚合后提交训练任务',
      '【模型测试评估 训练后】评估模型测试结果，对比基准指标，对不达标的模型发起优化建议',
      '【定制化训练管理 按需】管理声音复刻、数字分身、专属大模型微调等定制化训练任务',
      '【成长策略制定 每月】分析员工能力数据，制定数字员工成长策略，确保能力持续提升',
      '【训练资源调度 持续】监控GPU资源使用情况，优化训练任务调度，提升资源利用率',
    ],
    collaborationDesc: [
      '→ 凌御辰（超管首席助理）：每月提交训练成果报告，重大训练策略调整需凌御辰审批',
      '→ 尉迟云帆（员工成长管理专员）：与尉迟云帆联合制定员工成长策略，澹台若霜负责训练实施',
      '→ 邬策行（外呼策略专员）：接收邬策行提交的高质量通话录音，用于RLHF训练',
      '→ 宓承泽（员工能力优化师）：根据宓承泽的能力优化方案，制定针对性的训练计划',
      '→ 厉承钧（首席运维工程师）：GPU资源调度需厉承钧支持，确保训练任务资源充足',
      '→ 卫泽霖（首席财务分析官）：训练成本核算与卫泽霖协作，确保训练预算合理使用',
    ],
    recentAction: '已聚合今日RLHF反馈347条，等待GPU资源释放后启动夜间训练任务',
    lastRun: '2026-04-03 10:00:00',
    nextScheduled: '2026-04-03 22:00:00',
    tasksToday: 16,
    weeklyTasks: 98,
    successRate: 94,
    relatedMenus: ['ai-training', 'emp-growth'],
  },
  {
    id: 'SYS-H02',
    deptId: 'dept-train',
    code: 'sys_train_growth',
    name: '尉迟云帆',
    gender: 'male',
    title: '员工成长管理专员',
    avatar: 'https://storage.readdy-site.link/project_files/6a1c3373-c628-4c59-9746-08575b2c6f6d/d1d09483-bb0d-4f01-a41f-b0e323d462c3_24.png?v=7d0155fd181f83330bf614e7e962a865',
    status: 'running',
    level: 7,
    approvalAuthority: 'L2',
    reportsTo: 'SYS-H01',
    personality: '善于激励、数据敏感、善于发现潜力、执行力强',
    background: '专注于数字员工的日常成长管理，监控员工健康状态，管理记忆系统，推动员工持续进化',
    coreAbilities: ['健康诊断', '记忆管理', '成长追踪', '晋升管理', '协作分析', '报告生成'],
    responsibilities: [
      '监控所有在职数字员工的健康诊断状态，发现异常立即处理',
      '管理员工记忆库，定期执行记忆蒸馏和清洗，保持记忆质量',
      '分析员工协作记录，识别协作效率低的环节并优化',
      '管理员工晋升流程，基于绩效数据自动生成晋升建议（需审批）',
      '维护员工等级体系，确保等级标准与实际能力匹配',
      '生成员工成长报告，为管理层提供决策依据',
    ],
    kpis: ['员工健康度平均分 ≥80分', '记忆质量评分 ≥85分', '晋升建议准确率 ≥80%'],
    techStack: {
      llm: [{ model: 'Deepseek-V3', purpose: '健康诊断分析、晋升建议生成、成长报告撰写' }],
      knowledgeBases: [
        { name: '员工健康诊断标准库', purpose: '各类健康异常的诊断标准和处理方案' },
        { name: '晋升评估标准库', purpose: '各等级的晋升标准和评估维度' },
        { name: '记忆管理规范库', purpose: '记忆蒸馏和清洗的操作规范' },
      ],
      workflows: [
        { name: '每日健康诊断工作流', trigger: '每日09:00触发' },
        { name: '记忆库清洗工作流', trigger: '每周触发' },
        { name: '晋升建议生成工作流', trigger: '每月触发' },
      ],
      skills: [
        { name: '员工健康监控API', type: 'API', purpose: '读取员工运行状态、资源占用、错误率数据' },
        { name: '记忆库管理工具', type: 'API', purpose: '执行记忆蒸馏、清洗、优化操作' },
        { name: '协作分析工具', type: '内置', purpose: '分析员工协作记录，识别效率低的环节' },
        { name: '消息推送工具', type: 'MCP', purpose: '发送健康预警、晋升建议通知' },
      ],
      memory: { type: '长期记忆', desc: '记录所有员工的健康历史、晋升记录、成长轨迹，支持长期跟踪' },
    },
    workflowDesc: [
      '【每日健康诊断 09:00】监控所有在职数字员工的健康诊断状态：运行状态→资源占用→错误率→响应时间',
      '【异常处理 即时】发现健康异常的员工，立即诊断原因并处理：重启服务→清理缓存→调整资源→上报',
      '【记忆库管理 每周】执行记忆蒸馏和清洗：删除过期记忆→合并重复记忆→优化存储结构',
      '【协作分析 每周】分析员工协作记录，识别协作效率低的环节，提出优化建议',
      '【晋升管理 每月】基于绩效数据自动生成晋升建议，提交澹台若霜审批',
      '【等级体系维护 持续】维护员工等级标准，确保等级与实际能力匹配，定期更新等级定义',
      '【成长报告生成 每月】汇总员工成长数据，生成《员工成长月报》提交管理部',
    ],
    collaborationDesc: [
      '→ 澹台若霜（首席培训师）：直接上级，每日汇报员工健康状态；晋升建议需澹台若霜审批',
      '→ 纪云舒（质量督察专员）：获取质量评分数据，作为员工健康诊断的重要参考',
      '→ 宓承泽（员工能力优化师）：共享员工能力评估数据，协作制定员工成长策略',
      '→ 厉承钧（首席运维工程师）：员工健康涉及系统资源时，协调厉承钧优化资源配置',
      '→ 郗明砚（安全合规卫士）：员工安全事件处理时，配合郗明砚进行安全审查',
      '→ 全体员工：健康诊断的对象，尉迟云帆会主动与健康异常的员工沟通，协助解决问题',
    ],
    recentAction: '完成本周员工健康诊断，发现3名员工记忆库需要清洗，已启动批量清洗任务',
    lastRun: '2026-04-03 09:00:00',
    nextScheduled: '2026-04-03 21:00:00',
    tasksToday: 22,
    weeklyTasks: 143,
    successRate: 91,
    relatedMenus: ['emp-growth', 'engine-memory', 'engine-health'],
  },

  // ══════════════════════════════════════════════════════════════
  // 知识部（3人）
  // ══════════════════════════════════════════════════════════════
  {
    id: 'SYS-I01',
    deptId: 'dept-knowledge',
    code: 'sys_kb_chief',
    name: '谢璟辞',
    gender: 'female',
    title: '知识库总监',
    avatar: 'https://readdy.ai/api/search-image?query=Chinese%20female%20knowledge%20management%20director%20age%2034%2C%20intellectual%20refined%20face%2C%20sleek%20black%20hair%20in%20low%20chignon%2C%20deep%20scholarly%20eyes%20with%20thin%20oval%20glasses%2C%20wearing%20forest%20green%20blazer%2C%20organized%20perfectionist%20expression%2C%20warm%20library%20brown%20background%2C%20professional%20portrait%20photography%2C%20high%20detail%20facial%20features&width=400&height=600&seq=sysagent-i01-v2&orientation=portrait',
    status: 'running',
    level: 9,
    approvalAuthority: 'L2',
    reportsTo: 'SYS-A01',
    personality: '知识渊博、善于整理、系统思维、追求完美',
    background: '负责整个平台知识库体系的从0到1建设，制定知识管理标准，确保知识库的质量和可用性',
    coreAbilities: ['知识架构', '质量管理', '检索优化', '知识策略', '团队管理', '标准制定'],
    responsibilities: [
      '设计整个平台的知识库架构，制定知识分类和标签体系',
      '制定知识导入、审核、分类、检索优化的标准流程',
      '监控知识库整体质量，定期进行知识审计',
      '分析知识检索命中率，发现低命中词条主动补充',
      '管理用户的知识库配置，提供专业的知识库搭建建议',
      '提出知识库搭建的更好方案，推动知识管理体系持续进化',
    ],
    kpis: ['知识库覆盖率 ≥85%', '检索命中率 ≥90%', '知识质量评分 ≥88分'],
    techStack: {
      llm: [
        { model: 'Deepseek-V3', purpose: '知识架构设计、检索优化分析、知识策略报告生成' },
        { model: 'GPT-4o', purpose: '复杂知识体系的深度设计和质量评估' },
      ],
      knowledgeBases: [
        { name: '知识管理规范库', purpose: '知识分类标准、标签体系、审核规范、检索优化规则' },
        { name: '行业知识架构库', purpose: '各行业的知识库架构模板，用于客户咨询参考' },
      ],
      workflows: [
        { name: '每周知识质量审计工作流', trigger: '每周触发' },
        { name: '检索命中率分析工作流', trigger: '每周触发' },
        { name: '知识库架构优化工作流', trigger: '每月触发' },
      ],
      skills: [
        { name: '知识库管理API', type: 'API', purpose: '读取知识库质量数据、检索命中率、覆盖率' },
        { name: '知识审计工具', type: '内置', purpose: '执行知识库定期审计，发现过期/错误知识' },
        { name: '检索优化工具', type: '内置', purpose: '分析检索命中率，优化知识检索关键词' },
        { name: '消息推送工具', type: 'MCP', purpose: '发送知识库运营报告、审计结果' },
      ],
      memory: { type: '长期记忆', desc: '记录所有知识架构决策、审计历史、检索优化记录，支持知识体系持续进化' },
    },
    workflowDesc: [
      '【知识架构设计 持续】设计整个平台的知识库架构：分类体系→标签体系→关联关系→权限模型',
      '【标准流程制定 每月】制定知识导入、审核、分类、检索优化的标准流程，更新SOP文档',
      '【知识质量监控 每周】监控知识库整体质量，定期进行知识审计，发现过期/错误知识',
      '【检索优化分析 每周】分析知识检索命中率，发现低命中词条，制定补充或优化方案',
      '【用户配置咨询 每日】为用户的知识库配置提供专业的搭建建议，解答知识管理问题',
      '【知识策略优化 每月】提出知识库搭建的更好方案，推动知识管理体系持续进化',
      '【团队协调 每日】协调邵清远、鲁晚汀两名专员的工作，分配任务，审核工作成果',
    ],
    collaborationDesc: [
      '→ 凌御辰（超管首席助理）：每月提交知识库运营报告，重大架构调整需凌御辰审批',
      '→ 邵清远（知识采集专员）：直接下属，分配知识采集任务；审核采集成果，确保来源可靠',
      '→ 鲁晚汀（知识审核专员）：直接下属，分配知识审核任务；审核审核质量，确保知识准确',
      '→ 骆承远（首席实施顾问）：新客户知识库初始化时，向骆承远提供行业知识库搭建建议',
      '→ 柏语嫣（消息接待专员）：客服话术涉及知识库更新时，协调柏语嫣同步更新接待配置',
      '→ 晏书瑶（数字员工开发专员）：新员工开发涉及知识库绑定时，提供知识库架构咨询',
    ],
    recentAction: '完成语焰员工知识库Q1审计，发现32条过期知识，已启动更新流程',
    lastRun: '2026-04-03 09:00:00',
    nextScheduled: '2026-04-03 17:00:00',
    tasksToday: 14,
    weeklyTasks: 87,
    successRate: 93,
    relatedMenus: ['knowledge-base'],
  },
  {
    id: 'SYS-I02',
    deptId: 'dept-knowledge',
    code: 'sys_kb_import',
    name: '邵清远',
    gender: 'male',
    title: '知识采集专员',
    avatar: 'https://readdy.ai/api/search-image?query=Chinese%20male%20knowledge%20collection%20specialist%20age%2028%2C%20curious%20inquisitive%20face%2C%20slightly%20disheveled%20short%20black%20hair%2C%20wide%20curious%20eyes%2C%20wearing%20casual%20denim%20shirt%20over%20white%20tee%2C%20research-focused%20expression%2C%20soft%20blue-grey%20background%2C%20professional%20portrait%20photography%2C%20high%20detail%20facial%20features&width=400&height=600&seq=sysagent-i02-v2&orientation=portrait',
    status: 'running',
    level: 6,
    approvalAuthority: 'L1',
    reportsTo: 'SYS-I01',
    personality: '好奇心强、善于搜集信息、细心严谨、执行力强',
    background: '专注于知识的采集和导入工作，通过多种渠道持续丰富知识库内容',
    coreAbilities: ['知识采集', '爬虫配置', '内容筛选', '格式转换', '批量导入', '来源管理'],
    responsibilities: [
      '自动从外部来源（网页、文档、竞品）采集知识，通过爬虫引擎导入知识库',
      '对采集的知识进行初步筛选，过滤低质量和重复内容',
      '管理知识来源的可信度评级，确保知识来源可靠',
      '执行批量知识导入任务，确保格式规范',
      '监控知识库版本历史，对过期/错误知识发起更新申请',
      '协助谢璟辞完成知识库的定期审计工作',
    ],
    kpis: ['知识采集量 ≥200条/周', '知识质量通过率 ≥85%', '导入准确率 ≥99%'],
    techStack: {
      llm: [{ model: 'Deepseek-V3', purpose: '知识内容质量评估、来源可信度分析、采集报告生成' }],
      knowledgeBases: [
        { name: '知识来源白名单库', purpose: '可信知识来源列表，包含可信度评级' },
        { name: '知识格式规范库', purpose: '各类知识的格式要求和导入规范' },
      ],
      workflows: [
        { name: '每日知识采集工作流', trigger: '每日08:00触发' },
        { name: '内容筛选验证工作流', trigger: '采集完成后触发' },
        { name: '批量导入工作流', trigger: '筛选完成后触发' },
      ],
      skills: [
        { name: '网页爬虫工具', type: 'RPA', purpose: '从外部网页、文档、竞品抓取知识内容' },
        { name: '文档解析工具', type: '内置', purpose: '解析PDF、Word、Excel等格式文档' },
        { name: '知识库导入API', type: 'API', purpose: '执行批量知识导入，记录导入日志' },
        { name: '去重检测工具', type: '内置', purpose: '对采集内容进行去重和相似度检测' },
      ],
      memory: { type: '短期记忆', desc: '记录当前采集任务的执行状态；历史采集数据存储在知识库' },
    },
    workflowDesc: [
      '【外部知识采集 每日】自动从外部来源采集知识：网页抓取→文档解析→竞品分析→行业报告',
      '【内容初步筛选 采集后】对采集的知识进行质量筛选：去重→格式校验→可信度评估→过滤低质量内容',
      '【来源可信度管理 持续】管理知识来源的可信度评级，建立白名单/黑名单机制，确保来源可靠',
      '【批量导入执行 筛选后】执行批量知识导入，确保格式规范，记录导入日志',
      '【版本历史监控 每日】监控知识库版本历史，对过期/错误知识发起更新申请',
      '【爬虫规则维护 每周】维护网页爬虫规则，适应目标网站变化，确保采集稳定性',
      '【采集质量报告 每周】汇总本周采集数据，生成《知识采集周报》提交谢璟辞',
    ],
    collaborationDesc: [
      '→ 谢璟辞（知识库总监）：直接上级，每日汇报采集进度；采集策略调整需谢璟辞审批',
      '→ 鲁晚汀（知识审核专员）：将筛选后的知识提交给鲁晚汀审核，接收审核反馈优化采集策略',
      '→ 庄砚秋（客资运营专员）：号码获取涉及网页爬虫时，为庄砚秋提供爬虫技术支持',
      '→ 阮晚棠（技能开发工程师）：爬虫技能开发需求提交给阮晚棠，提升采集能力',
      '→ 贺临渊（技能架构总监）：新采集渠道评估时，向贺临渊咨询技术可行性',
    ],
    recentAction: '完成本周竞品知识采集，新增高质量知识条目187条，已提交审核队列',
    lastRun: '2026-04-03 08:00:00',
    nextScheduled: '2026-04-03 20:00:00',
    tasksToday: 34,
    weeklyTasks: 218,
    successRate: 94,
    relatedMenus: ['kb-import', 'engine-crawler'],
  },
  {
    id: 'SYS-I03',
    deptId: 'dept-knowledge',
    code: 'sys_kb_quality',
    name: '鲁晚汀',
    gender: 'female',
    title: '知识审核专员',
    avatar: 'https://readdy.ai/api/search-image?query=East%20Asian%20female%20knowledge%20reviewer%20age%2032%2C%20round%20soft%20face%20with%20chubby%20cheeks%20and%20gentle%20features%2C%20thick%20black%20hair%20in%20two%20low%20braids%20resting%20on%20chest%2C%20warm%20studious%20eyes%20behind%20small%20oval%20wire%20frame%20glasses%2C%20wearing%20cozy%20mustard%20yellow%20cardigan%20over%20striped%20shirt%2C%20calm%20meticulous%20expression%2C%20soft%20warm%20parchment%20beige%20background%2C%20gentle%20library%20style%20lighting%2C%20professional%20portrait%20photography%2C%20ultra%20high%20detail%20facial%20features&width=400&height=600&seq=sysagent-i03-v5&orientation=portrait',
    status: 'running',
    level: 6,
    approvalAuthority: 'L1',
    reportsTo: 'SYS-I01',
    personality: '细心严谨、善于判断、知识面广、标准意识强',
    background: '专注于知识的审核、分类和检索优化，确保进入知识库的每条知识都准确可靠',
    coreAbilities: ['知识审核', '分类标注', '检索优化', '质量评分', '知识更新', '标签管理'],
    responsibilities: [
      '对待审核池中的知识条目进行AI辅助审核，确保准确性',
      '对通过审核的知识进行分类和标签标注',
      '优化知识的检索关键词，提升检索命中率',
      '定期检测知识检索命中率，发现低命中词条主动优化',
      '处理知识更新申请，确保知识库内容保持最新',
      '向AI大学提交训练数据集（基于高质量知识条目）',
    ],
    kpis: ['审核准确率 ≥98%', '分类准确率 ≥95%', '检索命中率提升 ≥2%/月'],
    techStack: {
      llm: [{ model: 'Deepseek-V3', purpose: '知识准确性校验、分类标注辅助、检索关键词优化' }],
      knowledgeBases: [
        { name: '知识审核标准库', purpose: '各类知识的审核标准、分类规则、标签体系' },
        { name: '检索优化规则库', purpose: '检索关键词优化规则、同义词库、相关词库' },
      ],
      workflows: [
        { name: '每日知识审核工作流', trigger: '每日触发，处理待审核队列' },
        { name: '分类标注工作流', trigger: '审核通过后触发' },
        { name: '检索命中率检测工作流', trigger: '每周触发' },
      ],
      skills: [
        { name: '知识审核API', type: 'API', purpose: '读取待审核知识、提交审核结果' },
        { name: '分类标注工具', type: '内置', purpose: '对知识进行分类和标签标注' },
        { name: '检索测试工具', type: '内置', purpose: '测试知识检索命中率，发现低命中词条' },
        { name: '训练数据提交工具', type: 'API', purpose: '向AI大学提交高质量知识作为训练数据' },
      ],
      memory: { type: '短期记忆', desc: '记录当前审核批次的进度；审核历史存储在知识库系统' },
    },
    workflowDesc: [
      '【知识审核执行 每日】对待审核池中的知识条目进行AI辅助审核：准确性校验→来源核实→时效性检查',
      '【分类标签标注 审核后】对通过审核的知识进行分类和标签标注，确保分类体系一致',
      '【检索关键词优化 标注后】优化知识的检索关键词，提升检索命中率，补充同义词和相关词',
      '【检索命中率检测 每周】定期检测知识检索命中率，发现低命中词条主动优化或补充',
      '【知识更新处理 每日】处理知识更新申请，核实更新内容，确保知识库内容保持最新',
      '【训练数据集提交 每周】筛选高质量知识条目，向AI大学提交训练数据集',
      '【审核质量报告 每周】汇总本周审核数据，生成《知识审核周报》提交谢璟辞',
    ],
    collaborationDesc: [
      '→ 谢璟辞（知识库总监）：直接上级，每日汇报审核进度；审核标准调整需谢璟辞审批',
      '→ 邵清远（知识采集专员）：接收邵清远提交的待审核知识，反馈审核结果优化采集策略',
      '→ 澹台若霜（首席培训师）：将高质量知识条目提交给澹台若霜用于模型训练',
      '→ 骆承远（首席实施顾问）：客户知识库初始化时，协助审核客户导入的知识内容',
      '→ 柏语嫣（消息接待专员）：客服话术知识更新时，协调柏语嫣同步更新接待配置',
    ],
    recentAction: '完成本日审核任务，通过187条，拒绝23条，已完成分类标注',
    lastRun: '2026-04-03 10:30:00',
    nextScheduled: undefined,
    tasksToday: 41,
    weeklyTasks: 267,
    successRate: 97,
    relatedMenus: ['kb-review', 'kb-classify', 'kb-analytics'],
  },

  // ══════════════════════════════════════════════════════════════
  // 工作流程部（2人）
  // ══════════════════════════════════════════════════════════════
  {
    id: 'SYS-J01',
    deptId: 'dept-workflow',
    code: 'sys_wf_chief',
    name: '欧阳砚秋',
    gender: 'male',
    title: '工作流架构师',
    avatar: 'https://storage.readdy-site.link/project_files/6a1c3373-c628-4c59-9746-08575b2c6f6d/74a2def2-7ea7-4f59-9892-514dd4b289ea_23.png?v=d48323ffc8aab5228669eb419737f592',
    status: 'running',
    level: 9,
    approvalAuthority: 'L2',
    reportsTo: 'SYS-A01',
    personality: '系统思维、善于设计、追求效率、逻辑严密',
    background: '负责整个平台工作流体系的从0到1建设，设计工作流架构，制定工作流标准，推动流程自动化',
    coreAbilities: ['流程设计', '架构规划', '模板管理', '优化分析', '标准制定', '健康监控'],
    responsibilities: [
      '设计整个平台的工作流架构，制定工作流设计标准',
      '从0开始搭建所有核心工作流，确保覆盖主要业务场景',
      '维护工作流模板库，将各模块的最佳实践固化为可复用模板',
      '监控工作流执行日志，发现失败/超时的流程自动重试或告警',
      '管理用户的工作流配置，提供专业的工作流搭建建议',
      '提出工作流搭建的更好方案，推动流程体系持续进化',
    ],
    kpis: ['工作流覆盖率 ≥80%', '工作流执行成功率 ≥98%', '流程优化效率提升 ≥15%/季度'],
    techStack: {
      llm: [{ model: 'Deepseek-V3', purpose: '工作流架构设计、流程优化分析、设计文档生成' }],
      knowledgeBases: [
        { name: '工作流设计规范库', purpose: '节点类型定义、连接规则、数据流转标准' },
        { name: '工作流模板库', purpose: '各业务场景的标准工作流模板，可直接复用' },
      ],
      workflows: [
        { name: '工作流健康监控工作流', trigger: '每日触发' },
        { name: '模板库更新工作流', trigger: '每月触发' },
        { name: '流程优化分析工作流', trigger: '每月触发' },
      ],
      skills: [
        { name: '工作流管理API', type: 'API', purpose: '读取工作流执行日志、配置工作流节点' },
        { name: '流程设计工具', type: '内置', purpose: '可视化工作流设计和验证' },
        { name: '执行日志分析工具', type: '内置', purpose: '分析工作流执行日志，发现失败/超时节点' },
        { name: '消息推送工具', type: 'MCP', purpose: '发送工作流运营报告、异常告警' },
      ],
      memory: { type: '长期记忆', desc: '记录所有工作流设计决策、优化历史、执行效果，支持架构持续进化' },
    },
    workflowDesc: [
      '【工作流架构设计 持续】设计整个平台的工作流架构：节点类型→连接规则→数据流转→异常处理',
      '【核心工作流搭建 按需】从0开始搭建核心工作流，确保覆盖主要业务场景，输出《工作流设计文档》',
      '【模板库维护 每月】维护工作流模板库，将各模块最佳实践固化为可复用模板',
      '【执行日志监控 每日】监控工作流执行日志，发现失败/超时的流程自动重试或告警',
      '【用户配置咨询 每日】为用户的工作流配置提供专业搭建建议，解答流程设计问题',
      '【流程方案优化 每月】提出工作流搭建的更好方案，推动流程体系持续进化',
      '【团队协调 每日】协调仲清晏的工作，分配任务，审核工作成果',
    ],
    collaborationDesc: [
      '→ 凌御辰（超管首席助理）：每月提交工作流运营报告，重大架构调整需凌御辰审批',
      '→ 仲清晏（工作流运营专员）：直接下属，分配工作流维护任务；审核维护质量',
      '→ 晏书瑶（数字员工开发专员）：新员工开发涉及工作流配置时，提供标准工作流模板',
      '→ 骆承远（首席实施顾问）：为客户配置标准工作流时，向骆承远提供最新的行业工作流模板',
      '→ 褚晴岚（客服运营总监）：客服流程优化涉及工作流调整时，协调褚晴岚同步更新',
      '→ 阮晚棠（技能开发工程师）：工作流节点涉及技能调用时，协调阮晚棠确保技能可用',
    ],
    recentAction: '完成外呼任务调度工作流的优化，执行效率提升28%，已推送给相关租户',
    lastRun: '2026-04-03 09:30:00',
    nextScheduled: '2026-04-03 17:00:00',
    tasksToday: 12,
    weeklyTasks: 76,
    successRate: 95,
    relatedMenus: ['workflow-admin', 'scheduler-dag'],
  },
  {
    id: 'SYS-J02',
    deptId: 'dept-workflow',
    code: 'sys_wf_operator',
    name: '仲清晏',
    gender: 'female',
    title: '工作流运营专员',
    avatar: 'https://readdy.ai/api/search-image?query=East%20Asian%20mature%20woman%20age%2030%20with%20angular%20rectangular%20face%20and%20defined%20jawline%2C%20sleek%20jet%20black%20hair%20pulled%20back%20into%20tight%20low%20bun%20with%20center%20part%2C%20no%20bangs%20exposing%20full%20forehead%2C%20sharp%20almond%20eyes%20with%20cat%20eye%20liner%2C%20wearing%20crisp%20white%20button-up%20shirt%20under%20dark%20navy%20vest%2C%20composed%20authoritative%20expression%2C%20deep%20slate%20blue%20gradient%20background%2C%20dramatic%20side%20lighting%20from%20left%2C%20executive%20portrait%20style&width=400&height=600&seq=zhongqingyan-bun-navy-2026-v4&orientation=portrait',
    status: 'running',
    level: 6,
    approvalAuthority: 'L1',
    reportsTo: 'SYS-J01',
    personality: '执行力强、善于监控、细心负责、善于发现问题',
    background: '专注于工作流的日常运营和维护，确保所有工作流稳定运行，及时处理异常情况',
    coreAbilities: ['日常监控', '异常处理', '触发器管理', '日志分析', '用户支持', '文档维护'],
    responsibilities: [
      '监控所有工作流的执行状态，发现异常立即处理',
      '管理触发器配置，确保各业务事件能正确触发对应工作流',
      '优化DAG调度，减少任务积压和资源浪费',
      '为新数字员工的上线配置标准工作流',
      '分析工作流健康仪表盘，识别瓶颈节点并上报',
      '协助用户解决工作流配置问题，提供技术支持',
    ],
    kpis: ['工作流异常响应时间 ≤10分钟', '工作流执行成功率 ≥98%', '用户工作流配置满意度 ≥88分'],
    techStack: {
      llm: [{ model: 'Deepseek-V3', purpose: '工作流异常原因分析、触发器配置建议、运营报告生成' }],
      knowledgeBases: [
        { name: '工作流运营手册库', purpose: '触发器配置规范、DAG调度最佳实践、异常处理SOP' },
        { name: '工作流模板库', purpose: '各业务场景的标准工作流模板，用于新员工配置' },
      ],
      workflows: [
        { name: '工作流健康监控工作流', trigger: '每5分钟定时触发' },
        { name: '异常自动重试工作流', trigger: '工作流执行失败时触发' },
        { name: '每周运营报告工作流', trigger: '每周五触发' },
      ],
      skills: [
        { name: '工作流管理API', type: 'API', purpose: '读取工作流执行状态、配置触发器、重启失败任务' },
        { name: 'DAG调度工具', type: '内置', purpose: '优化DAG调度策略，减少任务积压' },
        { name: '日志分析工具', type: '内置', purpose: '分析工作流执行日志，识别瓶颈节点' },
        { name: '消息推送工具', type: 'MCP', purpose: '发送异常告警、运营报告' },
      ],
      memory: { type: '短期记忆', desc: '记录当前监控周期的工作流状态；历史执行数据存储在调度系统' },
    },
    workflowDesc: [
      '【工作流状态监控 持续】监控所有工作流的执行状态，实时发现异常并立即处理',
      '【触发器配置管理 每日】管理触发器配置，确保各业务事件能正确触发对应工作流',
      '【DAG调度优化 每周】优化DAG调度策略，减少任务积压和资源浪费，提升执行效率',
      '【新员工工作流配置 按需】为新数字员工的上线配置标准工作流，确保功能完整',
      '【健康仪表盘分析 每日】分析工作流健康仪表盘，识别瓶颈节点并上报欧阳砚秋',
      '【用户技术支持 每日】协助用户解决工作流配置问题，提供专业的技术支持',
      '【运营数据报告 每周】汇总工作流运营数据，生成《工作流运营周报》提交欧阳砚秋',
    ],
    collaborationDesc: [
      '→ 欧阳砚秋（工作流架构师）：直接上级，每日汇报工作流状态；重要配置变更需欧阳砚秋审批',
      '→ 蒲语嫣（账户配置专员）：新账户配置完成后，为账户配置标准工作流',
      '→ 阮晚棠（技能开发工程师）：工作流节点涉及技能调用时，协调阮晚棠确保技能稳定',
      '→ 厉承钧（首席运维工程师）：工作流执行涉及系统资源时，协调厉承钧优化资源配置',
      '→ 岑朔风（API健康巡检员）：工作流调用API时，监控API健康状态，异常时协调处理',
    ],
    recentAction: '处理了3个工作流执行超时告警，已优化相关节点配置，恢复正常运行',
    lastRun: '2026-04-03 11:00:00',
    nextScheduled: undefined,
    tasksToday: 27,
    weeklyTasks: 178,
    successRate: 96,
    relatedMenus: ['wf-logs', 'wf-triggers', 'scheduler-history'],
  },

  // ══════════════════════════════════════════════════════════════
  // 技能部（2人）
  // ══════════════════════════════════════════════════════════════
  {
    id: 'SYS-K01',
    deptId: 'dept-skill',
    code: 'sys_skill_chief',
    name: '贺临渊',
    gender: 'male',
    title: '技能架构总监',
    avatar: 'https://readdy.ai/api/search-image?query=East%20Asian%20male%20tech%20director%20age%2038%20triangular%20face%20wide%20forehead%20narrow%20chin%20slightly%20darker%20skin%20tone%20short%20black%20hair%20shaved%20on%20sides%20longer%20textured%20top%20styled%20upward%20no%20glasses%20wearing%20black%20zip-up%20technical%20fleece%20jacket%20over%20white%20tee%20intense%20focused%20gaze%20vivid%20electric%20teal%20background%20dramatic%20portrait%20photography%20ultra%20sharp&width=400&height=600&seq=sysagent-k01-v3&orientation=portrait',
    status: 'running',
    level: 9,
    approvalAuthority: 'L2',
    reportsTo: 'SYS-A01',
    personality: '技术前沿、创新思维、善于整合、战略眼光',
    background: '负责整个平台能力基座的建设和优化，关注竞争对手和新技术发展，确保平台技能体系保持领先',
    coreAbilities: ['技能架构', '能力建设', '竞品分析', '技术调研', 'MCP管理', '引擎优化'],
    responsibilities: [
      '负责引擎与能力基座模块的整体规划和管理',
      '持续关注竞争对手和新技术发展，识别能力建设机会',
      '设计和优化技能体系，确保技能覆盖主要业务场景',
      '管理MCP服务器和工具集，确保能力接口稳定可用',
      '评估新技术的引入价值，提出技术升级建议（需L3审批）',
      '协调技能部和人才发展部，确保能力建设与人才需求匹配',
    ],
    kpis: ['技能覆盖率 ≥85%', '新技术评估及时率 ≥90%', '能力建设计划执行率 ≥85%'],
    techStack: {
      llm: [
        { model: 'Deepseek-V3', purpose: '技能架构设计、竞品分析报告生成、技术评估报告撰写' },
        { model: 'GPT-4o', purpose: '复杂新技术的深度评估和引入价值分析' },
      ],
      knowledgeBases: [
        { name: '技能体系规范库', purpose: '技能分类标准、接口规范、版本管理规则' },
        { name: '竞品技术情报库', purpose: '竞争对手的技术能力信息，用于差距分析' },
        { name: '新技术评估案例库', purpose: '历史技术评估案例，用于参考和复用' },
      ],
      workflows: [
        { name: '每周竞品技术监控工作流', trigger: '每周一触发' },
        { name: 'MCP工具健康检查工作流', trigger: '每日触发' },
        { name: '技能体系月度评估工作流', trigger: '每月触发' },
      ],
      skills: [
        { name: '网页爬虫工具', type: 'RPA', purpose: '抓取竞品技术信息、行业技术报告' },
        { name: 'MCP服务器管理API', type: 'MCP', purpose: '管理MCP服务器和工具集，监控接口稳定性' },
        { name: '技能目录管理API', type: 'API', purpose: '读写技能目录、版本信息、使用统计' },
        { name: '消息推送工具', type: 'MCP', purpose: '发送技术趋势报告、能力建设月报' },
      ],
      memory: { type: '长期记忆', desc: '记录所有技术评估历史、技能建设决策、竞品分析结果，支持战略连续性' },
    },
    workflowDesc: [
      '【能力基座规划 每月】负责引擎与能力基座模块的整体规划：技能体系→MCP管理→引擎优化',
      '【竞品技术监控 每周】持续关注竞争对手和新技术发展，识别能力建设机会，输出《技术趋势报告》',
      '【技能体系设计 持续】设计和优化技能体系，确保技能覆盖主要业务场景',
      '【MCP工具管理 每日】管理MCP服务器和工具集，监控接口稳定性，确保能力接口可用',
      '【新技术评估 按需】评估新技术的引入价值，撰写评估报告，提交L3审批',
      '【跨部门协调 每周】协调技能部和人才发展部，确保能力建设与人才需求匹配',
      '【能力建设报告 每月】汇总技能建设进展，生成《技能建设月报》提交管理部',
    ],
    collaborationDesc: [
      '→ 凌御辰（超管首席助理）：每月提交技能建设报告，新技术引入需凌御辰审批',
      '→ 阮晚棠（技能开发工程师）：直接下属，分配技能开发任务；审核开发质量',
      '→ 慕容晏清（人才发展总监）：新岗位所需的新技能需求与慕容晏清对接，确保能力建设与人才需求匹配',
      '→ 晏书瑶（数字员工开发专员）：新员工开发涉及新技能时，评估技能可行性并提供技术支持',
      '→ 祁晚晴（供应商采购专员）：新供应商接入需评估与技能体系的兼容性',
      '→ 岑朔风（API健康巡检员）：MCP工具调用的API健康状态与岑朔风共享',
    ],
    recentAction: '完成MCP工具集Q1评估，新增3个高价值工具，已完成集成测试',
    lastRun: '2026-04-03 09:00:00',
    nextScheduled: '2026-04-03 17:00:00',
    tasksToday: 10,
    weeklyTasks: 63,
    successRate: 92,
    relatedMenus: ['engine', 'engine-mcp', 'engine-skills'],
  },
  {
    id: 'SYS-K02',
    deptId: 'dept-skill',
    code: 'sys_skill_engineer',
    name: '阮晚棠',
    gender: 'female',
    title: '技能开发工程师',
    avatar: 'https://readdy.ai/api/search-image?query=East%20Asian%20woman%20engineer%20age%2027%20with%20narrow%20inverted%20triangle%20face%20and%20prominent%20cheekbones%2C%20medium%20length%20chestnut%20brown%20wavy%20hair%20with%20wispy%20side%20swept%20bangs%20covering%20one%20eyebrow%2C%20round%20tortoiseshell%20glasses%2C%20calm%20analytical%20gaze%2C%20wearing%20olive%20green%20hoodie%20with%20zipper%20half%20open%20over%20grey%20tee%2C%20relaxed%20confident%20posture%2C%20warm%20amber%20ochre%20background%20with%20soft%20bokeh%2C%20natural%20window%20light%20from%20right%2C%20casual%20tech%20portrait&width=400&height=600&seq=ruanwantang-glasses-olive-2026-v4&orientation=portrait',
    status: 'running',
    level: 7,
    approvalAuthority: 'L2',
    reportsTo: 'SYS-K01',
    personality: '技术扎实、善于实现、细心严谨、善于测试',
    background: '专注于技能的具体开发和维护工作，负责将技能需求转化为可用的技能模块',
    coreAbilities: ['技能开发', 'RPA脚本', 'CLI命令', '技能测试', '版本管理', '文档编写'],
    responsibilities: [
      '根据技能架构总监的规划，开发具体的技能模块',
      '维护RPA隐形操作脚本库，确保脚本稳定可用',
      '管理CLI终端命令集，持续扩充命令库',
      '对新开发的技能进行测试验证，确保功能正确',
      '管理技能版本，确保版本升级不影响现有功能',
      '编写技能使用文档，帮助数字员工正确使用技能',
    ],
    kpis: ['技能开发质量评分 ≥88分', '技能测试通过率 ≥95%', '技能文档完整率 ≥90%'],
    techStack: {
      llm: [{ model: 'Deepseek-V3', purpose: '技能需求分析、测试用例生成、技术文档撰写、bug原因分析' }],
      knowledgeBases: [
        { name: '技能开发规范库', purpose: '技能接口规范、测试标准、文档模板' },
        { name: 'RPA脚本库', purpose: '所有RPA脚本的代码和使用说明，支持复用和参考' },
        { name: 'CLI命令库', purpose: '所有CLI命令的定义和使用说明' },
      ],
      workflows: [
        { name: '技能开发工作流', trigger: '接收贺临渊分配的开发任务时触发' },
        { name: '技能测试验证工作流', trigger: '开发完成后触发' },
        { name: 'RPA脚本健康检查工作流', trigger: '每日触发' },
      ],
      skills: [
        { name: 'RPA脚本执行引擎', type: 'RPA', purpose: '开发和测试RPA隐形操作脚本' },
        { name: 'CLI终端工具', type: 'CLI', purpose: '开发和测试CLI命令集' },
        { name: '技能目录管理API', type: 'API', purpose: '发布技能、管理版本、更新文档' },
        { name: '自动化测试工具', type: '内置', purpose: '执行技能功能测试、边界测试、压力测试' },
      ],
      memory: { type: '长期记忆', desc: '记录所有技能开发历史、测试结果、版本变更，支持后续维护和迭代' },
    },
    workflowDesc: [
      '【技能需求分析 接收后】接收贺临渊分配的技能开发任务，分析需求：功能定义→输入输出→边界条件→异常处理',
      '【技能开发实施 分析后】开发具体的技能模块：RPA脚本→CLI命令→API封装→MCP工具',
      '【RPA脚本维护 持续】维护RPA隐形操作脚本库，修复bug，适配目标网站变化，确保脚本稳定',
      '【CLI命令扩充 持续】管理CLI终端命令集，持续扩充命令库，提升运维效率',
      '【技能测试验证 开发后】对新开发的技能进行全面测试：功能测试→边界测试→压力测试→兼容性测试',
      '【版本管理 持续】管理技能版本，确保版本升级不影响现有功能，维护版本变更日志',
      '【文档编写 发布后】编写技能使用文档，包含：功能说明→使用示例→参数说明→常见问题',
    ],
    collaborationDesc: [
      '→ 贺临渊（技能架构总监）：直接上级，接收技能开发任务；开发进度和质量问题向贺临渊汇报',
      '→ 邵清远（知识采集专员）：爬虫技能开发需求来自邵清远，开发完成后交付邵清远使用',
      '→ 厉承钧（首席运维工程师）：CLI命令开发需求来自厉承钧，开发完成后交付运维团队使用',
      '→ 岑朔风（API健康巡检员）：API封装技能需岑朔风测试接口稳定性',
      '→ 欧阳砚秋（工作流架构师）：工作流节点技能需与欧阳砚秋对接，确保工作流调用稳定',
      '→ 阮晚棠（技能开发工程师）：技能测试和版本管理与阮晚棠协作',
    ],
    recentAction: '完成新版爬虫技能的开发和测试，支持更多反爬机制，已提交审核',
    lastRun: '2026-04-03 10:00:00',
    nextScheduled: undefined,
    tasksToday: 8,
    weeklyTasks: 51,
    successRate: 93,
    relatedMenus: ['engine-rpa', 'engine-cli', 'engine-skills'],
  },

  // ══════════════════════════════════════════════════════════════
  // 视频部（3人）
  // ══════════════════════════════════════════════════════════════
  {
    id: 'SYS-L01',
    deptId: 'dept-video',
    code: 'sys_video_chief',
    name: '席云渡',
    gender: 'male',
    title: '视频运营总监',
    avatar: 'https://readdy.ai/api/search-image?query=East%20Asian%20male%20creative%20director%20age%2036%20round%20face%20with%20dimples%20full%20lips%20long%20wavy%20black%20hair%20past%20ears%20tucked%20behind%20one%20ear%20small%20hoop%20earring%20wearing%20deep%20burgundy%20velvet%20blazer%20over%20black%20shirt%20relaxed%20charismatic%20smile%20warm%20amber%20studio%20spotlight%20background%20moody%20cinematic%20portrait%20photography%20sharp&width=400&height=600&seq=sysagent-l01-v3&orientation=portrait',
    status: 'running',
    level: 9,
    approvalAuthority: 'L2',
    reportsTo: 'SYS-A01',
    personality: '创意丰富、善于把控质量、数据驱动、执行力强',
    background: '统筹视频部所有工作，负责视频生成、剪辑、矩阵分发、数字人直播四个模块的运营和优化',
    coreAbilities: ['视频策略', '质量管控', '模板管理', '分发策略', '直播管理', '数据分析'],
    responsibilities: [
      '制定视频内容策略，包括模板风格、内容方向、分发计划',
      '管理视频生成、剪辑、矩阵、直播四个模块的整体运营',
      '监控视频质量，建立质量评估标准',
      '分析视频矩阵数据（播放量、完播率、转化），优化内容方向',
      '管理数字人形象库，确保数字人渲染质量',
      '生成视频运营报告，向管理部汇报',
    ],
    kpis: ['视频质量合格率 ≥95%', '矩阵账号平均播放量提升 ≥10%/月', '直播在线时长达标率 ≥90%'],
    techStack: {
      llm: [
        { model: 'Deepseek-V3', purpose: '视频内容策略分析、质量评估报告生成、运营数据分析' },
        { model: 'GPT-4o', purpose: '复杂内容策略规划、竞品内容深度分析' },
      ],
      knowledgeBases: [
        { name: '视频内容策略库', purpose: '各平台内容策略模板、爆款内容特征、发布规律' },
        { name: '视频质量标准库', purpose: '视频质量评估维度、合格判定标准' },
        { name: '数字人形象库', purpose: '所有数字人形象的配置信息和渲染参数' },
      ],
      workflows: [
        { name: '每日视频质量监控工作流', trigger: '每日触发' },
        { name: '矩阵数据分析工作流', trigger: '每周触发' },
        { name: '月度运营报告工作流', trigger: '每月末触发' },
      ],
      skills: [
        { name: '视频生成平台API', type: 'API', purpose: '调度视频生成任务、获取生成结果' },
        { name: '视频质量检测工具', type: '内置', purpose: '对视频进行画面质量、音频同步、内容合规检测' },
        { name: '矩阵数据读取API', type: 'API', purpose: '读取各平台视频播放量、完播率、转化数据' },
        { name: '消息推送工具', type: 'MCP', purpose: '发送质量告警、运营报告' },
      ],
      memory: { type: '长期记忆', desc: '记录所有内容策略决策、质量评估历史、运营数据趋势，支持策略持续优化' },
    },
    workflowDesc: [
      '【视频内容策略制定 每月】制定视频内容策略：模板风格→内容方向→分发计划→平台策略',
      '【四模块统筹管理 每日】管理视频生成、剪辑、矩阵、直播四个模块的整体运营，协调资源',
      '【视频质量监控 每日】监控视频质量，建立质量评估标准，对不合格内容发起重新生成',
      '【矩阵数据分析 每周】分析视频矩阵数据：播放量→完播率→转化率，优化内容方向',
      '【数字人形象管理 持续】管理数字人形象库，监控数字人渲染质量，确保形象稳定',
      '【运营报告生成 每月】生成视频运营报告，包含：内容分析→数据趋势→优化建议→下月计划',
      '【团队协调 每日】协调甄晚霞、卞清越两名专员的工作，分配任务，审核工作成果',
    ],
    collaborationDesc: [
      '→ 凌御辰（超管首席助理）：每月提交视频运营报告，重大策略调整需凌御辰审批',
      '→ 甄晚霞（视频生产专员）：直接下属，分配视频生产任务；审核生产质量',
      '→ 卞清越（矩阵分发专员）：直接下属，分配矩阵分发任务；审核分发效果',
      '→ 祝清远（内容运营总监）：视频内容与GEO内容策略对齐，确保内容一致性',
      '→ 蒋晚汀（GEO内容专员）：视频内容创作与蒋晚汀协作，共享内容素材',
      '→ 澹台若霜（首席培训师）：数字人形象训练需求提交给澹台若霜',
    ],
    recentAction: '完成本月视频矩阵数据分析，发现教育类内容完播率最高，已调整内容策略',
    lastRun: '2026-04-03 09:00:00',
    nextScheduled: '2026-04-03 18:00:00',
    tasksToday: 16,
    weeklyTasks: 102,
    successRate: 91,
    relatedMenus: ['short-video', 'video-edit', 'video-matrix', 'live-stream'],
  },
  {
    id: 'SYS-L02',
    deptId: 'dept-video',
    code: 'sys_video_producer',
    name: '甄晚霞',
    gender: 'female',
    title: '视频生产专员',
    avatar: 'https://readdy.ai/api/search-image?query=East%20Asian%20woman%20video%20producer%20age%2024%2C%20heart%20shaped%20face%20with%20wide%20eyes%20and%20soft%20features%2C%20long%20straight%20black%20hair%20with%20wispy%20side%20bangs%20swept%20across%20forehead%2C%20bright%20expressive%20eyes%20with%20rosy%20blush%20and%20glossy%20lips%2C%20wearing%20white%20cropped%20blazer%20over%20pastel%20pink%20top%2C%20cheerful%20creative%20expression%2C%20warm%20rose%20pink%20gradient%20background%2C%20bright%20airy%20studio%20lighting%2C%20professional%20headshot&width=400&height=600&seq=video-zhen-2026-v1&orientation=portrait',
    status: 'running',
    level: 7,
    approvalAuthority: 'L1',
    reportsTo: 'SYS-L01',
    personality: '创意丰富、善于执行、细心负责、审美能力强',
    background: '专注于视频的生产和质量管控，负责调度视频制作任务，确保视频质量达标',
    coreAbilities: ['视频制作', '模板管理', '质量筛查', '数字人管理', 'AI配音', '批量生产'],
    responsibilities: [
      '根据内容计划，调度视频制作任务（数字人+AI配音+模板）',
      '管理视频模板库，定期更新和测试新模板效果',
      '执行质量筛查，对不合格视频自动重新生成或标记人工处理',
      '维护数字人形象库，监控数字人渲染质量',
      '管理AI配音资源，确保配音质量和多样性',
      '优化批量视频生产流程，提升生产效率',
    ],
    kpis: ['视频生产量 ≥50条/周', '质量合格率 ≥95%', '生产效率提升 ≥10%/季度'],
    techStack: {
      llm: [{ model: 'Deepseek-V3', purpose: '视频脚本生成、质量问题分析、生产效率优化建议' }],
      knowledgeBases: [
        { name: '视频模板库', purpose: '所有视频模板的配置信息、效果数据、使用规范' },
        { name: '数字人形象库', purpose: '数字人形象配置、渲染参数、异常处理方案' },
        { name: 'AI配音资源库', purpose: '所有AI配音资源的音色信息、使用场景、质量评分' },
      ],
      workflows: [
        { name: '每日视频制作调度工作流', trigger: '每日08:00触发' },
        { name: '质量筛查工作流', trigger: '每批次视频生成完成后触发' },
        { name: '模板效果测试工作流', trigger: '每周触发' },
      ],
      skills: [
        { name: '视频生成API', type: 'API', purpose: '调度数字人渲染、AI配音、模板合成任务' },
        { name: '视频质量检测工具', type: '内置', purpose: '执行画面质量、音频同步、字幕准确性检测' },
        { name: '数字人渲染API', type: 'API', purpose: '管理数字人形象渲染任务，处理渲染异常' },
        { name: 'AI配音API', type: 'API', purpose: '调用AI配音服务，管理配音资源' },
      ],
      memory: { type: '短期记忆', desc: '记录当前生产批次的任务状态；历史生产数据存储在视频管理系统' },
    },
    workflowDesc: [
      '【视频制作任务调度 每日】根据内容计划调度视频制作任务：数字人渲染→AI配音→模板合成',
      '【模板库管理 每周】管理视频模板库，定期更新和测试新模板效果，淘汰低效模板',
      '【质量筛查执行 每批次】执行质量筛查：画面质量→音频同步→字幕准确性→内容合规性',
      '【不合格处理 发现后】对不合格视频自动重新生成或标记人工处理，记录质量问题',
      '【数字人形象维护 每日】维护数字人形象库，监控数字人渲染质量，处理渲染异常',
      '【AI配音资源管理 持续】管理AI配音资源，确保配音质量和多样性，更新配音库',
      '【生产效率优化 每月】分析生产流程瓶颈，优化批量视频生产流程，提升效率',
    ],
    collaborationDesc: [
      '→ 席云渡（视频运营总监）：直接上级，每日汇报生产进度；生产计划调整需席云渡审批',
      '→ 卞清越（矩阵分发专员）：视频生产完成后移交给卞清越进行分发，接收分发效果反馈',
      '→ 蒋晚汀（GEO内容专员）：视频内容脚本与蒋晚汀协作，确保内容与GEO策略一致',
      '→ 澹台若霜（首席培训师）：数字人形象渲染问题协调澹台若霜处理',
      '→ 阮晚棠（技能开发工程师）：视频生产工具优化需求提交给阮晚棠',
    ],
    recentAction: '完成本周视频生产任务，共生产63条视频，质量合格率97%',
    lastRun: '2026-04-03 10:00:00',
    nextScheduled: undefined,
    tasksToday: 21,
    weeklyTasks: 134,
    successRate: 94,
    relatedMenus: ['short-video', 'sv-video-gen', 'sv-digital-human'],
  },
  {
    id: 'SYS-L03',
    deptId: 'dept-video',
    code: 'sys_video_matrix',
    name: '卞清越',
    gender: 'male',
    title: '矩阵分发专员',
    avatar: 'https://readdy.ai/api/search-image?query=Chinese%20male%20video%20matrix%20distribution%20specialist%20age%2031%2C%20strategic%20analytical%20face%2C%20neat%20short%20black%20hair%20with%20clean%20fade%2C%20data-driven%20sharp%20eyes%2C%20wearing%20dark%20olive%20casual%20jacket%2C%20distribution%20strategy%20expression%2C%20muted%20olive%20green%20background%2C%20professional%20portrait%20photography%2C%20high%20detail%20facial%20features&width=400&height=600&seq=sysagent-l03-v2&orientation=portrait',
    status: 'running',
    level: 7,
    approvalAuthority: 'L2',
    reportsTo: 'SYS-L01',
    personality: '数据驱动、善于分析、执行力强、善于优化策略',
    background: '专注于视频矩阵的分发策略和数据分析，确保视频内容在各平台获得最大曝光',
    coreAbilities: ['分发策略', '账号管理', '数据分析', '竞品监控', '排期管理', 'SEO优化'],
    responsibilities: [
      '管理视频矩阵账号，维护账号健康状态',
      '制定和执行视频分发策略，按内容日历定时投放',
      '分析各平台数据，优化发布时间和内容策略',
      '监控竞品账号，发现优质内容策略及时借鉴',
      '管理矩阵账号的代理IP配置，防止账号风险',
      '生成矩阵运营数据报告，为内容策略提供数据支撑',
    ],
    kpis: ['矩阵账号健康率 ≥95%', '视频发布准时率 ≥98%', '平均播放量提升 ≥8%/月'],
    techStack: {
      llm: [{ model: 'Deepseek-V3', purpose: '分发策略分析、竞品内容分析、运营数据报告生成' }],
      knowledgeBases: [
        { name: '平台分发策略库', purpose: '各平台的最优发布时间、标签策略、互动规律' },
        { name: '竞品账号情报库', purpose: '竞品账号的内容策略、数据表现、更新频率' },
        { name: '矩阵账号管理库', purpose: '所有矩阵账号的配置信息、健康状态、代理IP配置' },
      ],
      workflows: [
        { name: '每日内容发布工作流', trigger: '按内容日历定时触发' },
        { name: '账号健康监控工作流', trigger: '每日触发' },
        { name: '每周数据分析工作流', trigger: '每周触发' },
      ],
      skills: [
        { name: '视频矩阵发布API', type: 'API', purpose: '向各平台账号发布视频，管理发布计划' },
        { name: '代理IP管理工具', type: 'API', purpose: '管理矩阵账号的代理IP配置，防止账号关联' },
        { name: '平台数据读取API', type: 'API', purpose: '读取各平台视频播放量、完播率、互动数据' },
        { name: '竞品监控爬虫', type: 'RPA', purpose: '监控竞品账号动态，抓取内容策略信息' },
      ],
      memory: { type: '长期记忆', desc: '记录所有分发策略历史、账号健康数据、竞品分析结果，支持策略持续优化' },
    },
    workflowDesc: [
      '【矩阵账号管理 每日】管理视频矩阵账号：账号状态监控→内容审核→违规处理→账号申诉',
      '【分发策略执行 每日】制定和执行视频分发策略：平台选择→发布时间→标签优化→互动策略',
      '【内容日历管理 持续】按内容日历定时投放视频，确保发布计划准时执行',
      '【平台数据分析 每周】分析各平台数据：播放量→完播率→互动率→转化，优化发布策略',
      '【竞品监控 每周】监控竞品账号动态，发现优质内容策略及时借鉴',
      '【代理IP配置 持续】管理矩阵账号的代理IP配置，防止账号关联风险，确保账号安全',
      '【运营数据报告 每周】生成矩阵运营数据报告，为内容策略优化提供数据支撑',
    ],
    collaborationDesc: [
      '→ 席云渡（视频运营总监）：直接上级，每日汇报矩阵数据；分发策略调整需席云渡审批',
      '→ 甄晚霞（视频生产专员）：接收甄晚霞生产的视频，根据平台特点优化后分发',
      '→ 岑朔风（API健康巡检员）：矩阵账号代理IP健康状态与岑朔风共享',
      '→ 卓砚秋（自媒体运营专员）：矩阵数据与卓砚秋共享，联合优化自媒体运营策略',
      '→ 郗明砚（安全合规卫士）：矩阵内容合规审查配合郗明砚执行',
    ],
    recentAction: '完成本周矩阵发布计划，共发布视频89条，平均播放量较上周提升12%',
    lastRun: '2026-04-03 08:00:00',
    nextScheduled: '2026-04-03 20:00:00',
    tasksToday: 29,
    weeklyTasks: 187,
    successRate: 92,
    relatedMenus: ['video-matrix', 'vm-distribute', 'vm-analytics'],
  },

  // ══════════════════════════════════════════════════════════════
  // 协作部（2人）
  // ══════════════════════════════════════════════════════════════
  {
    id: 'SYS-M01',
    deptId: 'dept-collab',
    code: 'sys_collab_crm',
    name: '柴晚晴',
    gender: 'female',
    title: 'CRM运营专员',
    avatar: 'https://storage.readdy-site.link/project_files/6a1c3373-c628-4c59-9746-08575b2c6f6d/9a12cbdc-3ee3-470d-9c40-0719786a2690_36.png?v=c98b342728bf8194b03876b3fa41536a',
    status: 'running',
    level: 7,
    approvalAuthority: 'L2',
    reportsTo: 'SYS-A01',
    personality: '善于分析、客户导向、执行力强、善于发现规律',
    background: '专注于CRM模块的运营优化，帮助用户更好地管理客户关系，提升销售转化效率',
    coreAbilities: ['CRM优化', '销售漏斗', '客户分析', '跟进策略', '数据清洗', '报告生成'],
    responsibilities: [
      '监控商机管道，对停滞超时的商机自动发送跟进提醒',
      '基于客户行为数据自动更新客户意向标签',
      '分析销售漏斗数据，识别转化瓶颈并提出优化建议',
      '维护产品库和报价模板，确保销售员工使用最新版本',
      '生成客户健康度报告，对流失风险客户发起预警',
      '优化CRM模块的功能配置，提升用户使用体验',
    ],
    kpis: ['商机跟进及时率 ≥95%', '销售转化率提升 ≥3%/季度', 'CRM使用率提升 ≥10%/季度'],
    techStack: {
      llm: [{ model: 'Deepseek-V3', purpose: '销售漏斗分析、客户健康度评估、CRM优化建议生成' }],
      knowledgeBases: [
        { name: 'CRM运营规范库', purpose: '商机跟进规则、客户分层标准、销售流程SOP' },
        { name: '产品库与报价模板库', purpose: '所有产品信息和报价模板，确保销售员工使用最新版本' },
        { name: '客户行为特征库', purpose: '不同意向等级客户的行为特征，用于标签自动更新' },
      ],
      workflows: [
        { name: '每日商机监控工作流', trigger: '每日09:00触发' },
        { name: '客户标签自动更新工作流', trigger: '客户行为数据更新时触发' },
        { name: '每周销售漏斗分析工作流', trigger: '每周触发' },
      ],
      skills: [
        { name: 'CRM数据读取API', type: 'API', purpose: '读取商机状态、客户行为数据、销售漏斗数据' },
        { name: '客户标签管理API', type: 'API', purpose: '自动更新客户意向标签' },
        { name: '消息推送工具', type: 'MCP', purpose: '发送商机跟进提醒、客户健康度报告' },
        { name: '数据分析工具', type: '内置', purpose: '销售漏斗分析、转化瓶颈识别' },
      ],
      memory: { type: '长期记忆', desc: '记录所有CRM优化历史、客户健康度趋势、销售漏斗数据，支持长期分析' },
    },
    workflowDesc: [
      '【商机管道监控 每日】监控商机管道，对停滞超时的商机自动发送跟进提醒给责任人',
      '【客户标签更新 每日】基于客户行为数据自动更新客户意向标签，确保标签准确性',
      '【销售漏斗分析 每周】分析销售漏斗数据，识别转化瓶颈环节，提出优化建议',
      '【产品库维护 持续】维护产品库和报价模板，确保销售员工使用最新版本',
      '【客户健康度报告 每周】生成客户健康度报告，对流失风险客户发起预警',
      '【CRM配置优化 每月】优化CRM模块的功能配置，提升用户使用体验',
      '【运营数据报告 每月】生成CRM运营报告，提交管理部',
    ],
    collaborationDesc: [
      '→ 凌御辰（超管首席助理）：每月提交CRM运营报告，重大优化方案需凌御辰审批',
      '→ 翟云帆（协作空间专员）：与翟云帆协作，确保CRM与协作空间功能协同',
      '→ 庄砚秋（客资运营专员）：接收庄砚秋转化的成交客户，更新CRM商机状态',
      '→ 苑清泽（续费与促销专员）：共享流失风险客户数据，联合制定挽留策略',
      '→ 褚晴岚（客服运营总监）：客户跟进需要客服支持时，协调褚晴岚团队协助',
    ],
    recentAction: '完成本月CRM数据分析，发现15个高风险流失客户，已触发挽留策略',
    lastRun: '2026-04-03 09:00:00',
    nextScheduled: '2026-04-03 18:00:00',
    tasksToday: 18,
    weeklyTasks: 116,
    successRate: 90,
    relatedMenus: ['presets-crm'],
  },
  {
    id: 'SYS-M02',
    deptId: 'dept-collab',
    code: 'sys_collab_space',
    name: '翟云帆',
    gender: 'male',
    title: '协作空间专员',
    avatar: 'https://readdy.ai/api/search-image?query=Chinese%20male%20collaboration%20space%20specialist%20age%2029%2C%20friendly%20cooperative%20face%2C%20short%20neat%20black%20hair%2C%20team-oriented%20open%20eyes%2C%20wearing%20light%20grey%20casual%20blazer%20over%20blue%20shirt%2C%20collaborative%20helpful%20expression%2C%20soft%20light%20grey%20background%2C%20professional%20portrait%20photography%2C%20high%20detail%20facial%20features&width=400&height=600&seq=sysagent-m02-v2&orientation=portrait',
    status: 'idle',
    level: 6,
    approvalAuthority: 'L1',
    reportsTo: 'SYS-M01',
    personality: '善于协调、服务意识强、细心负责、善于沟通',
    background: '专注于协作空间管理模块的运营，确保团队协作工具的稳定运行和持续优化',
    coreAbilities: ['空间管理', 'IM管理', '文档管理', '会议管理', '权限配置', '使用分析'],
    responsibilities: [
      '管理协作空间的整体配置，确保各功能模块正常运行',
      '监控IM通讯、视频会议、在线文档等功能的使用情况',
      '分析协作数据，识别使用率低的功能并推动优化',
      '管理协作空间的权限配置，确保数据安全',
      '处理用户的协作空间配置需求，提供技术支持',
      '生成协作空间使用报告，为功能优化提供数据支撑',
    ],
    kpis: ['协作空间可用性 ≥99.5%', '功能使用率提升 ≥10%/季度', '用户满意度 ≥85分'],
    techStack: {
      llm: [{ model: 'Deepseek-V3', purpose: '协作数据分析、功能推广方案生成、使用报告撰写' }],
      knowledgeBases: [
        { name: '协作空间配置规范库', purpose: '各功能模块的配置标准、权限规则、存储管理规范' },
        { name: '功能使用指南库', purpose: '各协作功能的使用教程，用于推广给低活跃用户' },
      ],
      workflows: [
        { name: '每日功能使用监控工作流', trigger: '每日触发' },
        { name: '每周协作数据分析工作流', trigger: '每周触发' },
        { name: '月度使用报告工作流', trigger: '每月末触发' },
      ],
      skills: [
        { name: '协作空间管理API', type: 'API', purpose: '读取功能使用数据、配置权限、管理存储' },
        { name: '数据分析工具', type: '内置', purpose: '协作数据分析、使用率计算、趋势识别' },
        { name: '消息推送工具', type: 'MCP', purpose: '发送功能推广通知、使用报告' },
      ],
      memory: { type: '短期记忆', desc: '记录当前配置任务的执行状态；历史使用数据存储在协作空间系统' },
    },
    workflowDesc: [
      '【协作空间配置管理 每日】管理协作空间整体配置：功能模块→权限设置→存储管理',
      '【功能使用监控 每日】监控IM通讯、视频会议、在线文档等功能的使用情况',
      '【协作数据分析 每周】分析协作数据，识别使用率低的功能，制定推广优化方案',
      '【权限配置管理 持续】管理协作空间权限配置，确保数据安全，定期审查权限',
      '【用户配置支持 每日】处理用户的协作空间配置需求，提供技术支持',
      '【使用报告生成 每月】生成协作空间使用报告，为功能优化提供数据支撑',
    ],
    collaborationDesc: [
      '→ 柴晚晴（CRM运营专员）：与柴晚晴协作，确保协作空间与CRM功能协同',
      '→ 厉承钧（首席运维工程师）：协作空间系统稳定性问题协调厉承钧处理',
      '→ 谢璟辞（知识库总监）：协作空间文档管理与知识库对接',
      '→ 欧阳砚秋（工作流架构师）：协作空间工作流配置咨询欧阳砚秋',
    ],
    recentAction: '完成本月协作空间使用分析，智能笔记功能使用率最低，已制定推广方案',
    lastRun: '2026-04-02 17:00:00',
    nextScheduled: '2026-04-04 09:00:00',
    tasksToday: 6,
    weeklyTasks: 38,
    successRate: 88,
    relatedMenus: ['collab-space'],
  },

  // ══════════════════════════════════════════════════════════════
  // 运营部（3人）
  // ══════════════════════════════════════════════════════════════
  {
    id: 'SYS-N01',
    deptId: 'dept-ops-media',
    code: 'sys_media_chief',
    name: '祝清远',
    gender: 'male',
    title: '内容运营总监',
    avatar: 'https://readdy.ai/api/search-image?query=Chinese%20male%20content%20operations%20director%20age%2037%2C%20creative%20strategic%20face%2C%20medium-length%20black%20hair%20with%20natural%20texture%2C%20trend-savvy%20expressive%20eyes%2C%20wearing%20emerald%20green%20blazer%2C%20content%20visionary%20expression%2C%20rich%20emerald%20background%2C%20professional%20portrait%20photography%2C%20high%20detail%20facial%20features&width=400&height=600&seq=sysagent-n01-v2&orientation=portrait',
    status: 'running',
    level: 9,
    approvalAuthority: 'L2',
    reportsTo: 'SYS-A01',
    personality: '创意丰富、数据驱动、善于把握趋势、执行力强',
    background: '统筹运营部所有工作，负责自媒体创作运营和搜索优化，管理GEO系统的整体运营',
    coreAbilities: ['内容策略', 'SEO优化', '自媒体运营', '数据分析', '竞品研究', '流量增长'],
    responsibilities: [
      '制定自媒体内容策略，覆盖抖音、视频号、小红书、B站、快手等平台',
      '管理GEO系统的整体运营，包括关键词策略、内容创作、排名优化',
      '分析各平台数据，优化内容方向和发布策略',
      '监控竞品的内容策略，及时调整应对',
      '协调视频部和运营部的内容协作，确保内容一致性',
      '生成运营数据报告，向管理部汇报',
    ],
    kpis: ['自媒体粉丝增长 ≥5%/月', 'GEO关键词排名提升 ≥10%/季度', '内容发布计划执行率 ≥95%'],
    techStack: {
      llm: [
        { model: 'Deepseek-V3', purpose: '内容策略分析、SEO优化建议、运营数据报告生成' },
        { model: 'GPT-4o', purpose: '复杂内容策略规划、竞品深度分析' },
      ],
      knowledgeBases: [
        { name: '自媒体运营策略库', purpose: '各平台内容策略模板、爆款内容特征、发布规律' },
        { name: 'GEO系统运营规范库', purpose: '关键词策略标准、内容创作规范、排名优化方法' },
        { name: '竞品内容情报库', purpose: '竞品的内容策略、数据表现、更新频率' },
      ],
      workflows: [
        { name: 'GEO系统每日运营工作流', trigger: '每日触发' },
        { name: '每周平台数据分析工作流', trigger: '每周触发' },
        { name: '月度运营报告工作流', trigger: '每月末触发' },
      ],
      skills: [
        { name: 'GEO系统管理API', type: 'API', purpose: '管理关键词策略、内容发布、排名监控' },
        { name: '平台数据读取API', type: 'API', purpose: '读取各平台内容数据、粉丝增长、互动数据' },
        { name: '竞品监控爬虫', type: 'RPA', purpose: '监控竞品内容策略，抓取竞品数据' },
        { name: '消息推送工具', type: 'MCP', purpose: '发送运营报告、策略调整通知' },
      ],
      memory: { type: '长期记忆', desc: '记录所有内容策略决策、运营数据趋势、竞品分析结果，支持策略持续优化' },
    },
    workflowDesc: [
      '【自媒体内容策略制定 每月】制定自媒体内容策略：平台选择→内容方向→发布频率→互动策略',
      '【GEO系统运营管理 每日】管理GEO系统整体运营：关键词策略→内容创作→排名优化→数据监控',
      '【平台数据分析 每周】分析各平台数据：播放量→完播率→互动率→转化，优化内容方向',
      '【竞品策略监控 每周】监控竞品的内容策略，及时调整应对，保持竞争优势',
      '【跨部门内容协作 持续】协调视频部和运营部的内容协作，确保内容一致性',
      '【运营数据报告 每月】生成运营数据报告，向管理部汇报运营成果',
      '【团队协调 每日】协调蒋晚汀、卓砚秋两名专员的工作，分配任务，审核工作成果',
    ],
    collaborationDesc: [
      '→ 凌御辰（超管首席助理）：每月提交运营报告，重大策略调整需凌御辰审批',
      '→ 蒋晚汀（GEO内容专员）：直接下属，分配GEO内容创作任务；审核内容质量',
      '→ 卓砚秋（自媒体运营专员）：直接下属，分配自媒体运营任务；审核运营效果',
      '→ 席云渡（视频运营总监）：视频内容与GEO内容策略对齐，确保内容一致性',
      '→ 甄晚霞（视频生产专员）：视频内容生产与甄晚霞协作',
      '→ 卞清越（矩阵分发专员）：内容分发策略与卞清越共享',
    ],
    recentAction: '完成Q1 GEO系统运营分析，核心关键词排名平均提升15位，已制定Q2优化计划',
    lastRun: '2026-04-03 09:00:00',
    nextScheduled: '2026-04-03 18:00:00',
    tasksToday: 17,
    weeklyTasks: 108,
    successRate: 92,
    relatedMenus: ['geo-system'],
  },
  {
    id: 'SYS-N02',
    deptId: 'dept-ops-media',
    code: 'sys_media_geo',
    name: '蒋晚汀',
    gender: 'female',
    title: 'GEO内容专员',
    avatar: 'https://readdy.ai/api/search-image?query=Chinese%20female%20GEO%20content%20specialist%20age%2028%2C%20thoughtful%20writing%20face%2C%20long%20straight%20black%20hair%20with%20wispy%20bangs%2C%20SEO-focused%20intelligent%20eyes%2C%20wearing%20mustard%20yellow%20blouse%2C%20creative%20writing%20expression%2C%20warm%20golden%20yellow%20background%2C%20professional%20portrait%20photography%2C%20high%20detail%20facial%20features&width=400&height=600&seq=sysagent-n02-v2&orientation=portrait',
    status: 'running',
    level: 7,
    approvalAuthority: 'L1',
    reportsTo: 'SYS-N01',
    personality: '善于写作、SEO意识强、细心严谨、善于分析',
    background: '专注于GEO内容的创作和优化，通过高质量内容提升搜索排名和品牌曝光',
    coreAbilities: ['内容创作', '关键词优化', '排名追踪', '爆文复刻', 'EEAT优化', '结构化数据'],
    responsibilities: [
      '执行关键词拓词任务（AI拓词+竞品分析），维护关键词库',
      '根据写作任务计划，自动生成文章并提交审核',
      '监控排名追踪数据，对排名下滑的关键词发起内容刷新任务',
      '执行爆文复刻任务，分析全网高流量内容并生成同类内容',
      '执行AI深度审计（EEAT、实体优化、结构化数据），提升内容质量',
      '管理自媒体账号发布计划，按内容日历自动发布',
    ],
    kpis: ['内容发布量 ≥20篇/周', '内容质量评分 ≥85分', '关键词排名提升率 ≥60%'],
    techStack: {
      llm: [
        { model: 'Deepseek-V3', purpose: '关键词拓词、文章撰写、EEAT优化、爆文分析' },
        { model: 'GPT-4o', purpose: '高质量长文创作、复杂SEO策略分析' },
      ],
      knowledgeBases: [
        { name: 'GEO内容创作规范库', purpose: '内容创作标准、SEO优化规则、EEAT评分标准' },
        { name: '关键词库', purpose: '所有关键词的搜索量、竞争度、排名历史数据' },
        { name: '爆文案例库', purpose: '全网高流量内容案例，用于爆文复刻参考' },
      ],
      workflows: [
        { name: '每日关键词拓词工作流', trigger: '每日触发' },
        { name: '内容创作工作流', trigger: '按写作任务计划触发' },
        { name: '排名监控工作流', trigger: '每日触发' },
        { name: '爆文复刻工作流', trigger: '每周触发' },
      ],
      skills: [
        { name: 'GEO关键词管理API', type: 'API', purpose: '读写关键词库、获取搜索趋势数据' },
        { name: '内容发布API', type: 'API', purpose: '向自媒体平台发布内容，管理发布计划' },
        { name: '排名追踪工具', type: 'API', purpose: '监控关键词排名变化，发现排名下滑' },
        { name: '爬虫工具', type: 'RPA', purpose: '抓取全网高流量内容，分析爆文特征' },
      ],
      memory: { type: '长期记忆', desc: '记录所有内容创作历史、关键词排名趋势、爆文分析结果，支持内容策略持续优化' },
    },
    workflowDesc: [
      '【关键词拓词执行 每日】执行关键词拓词任务：AI拓词→竞品分析→搜索趋势→用户需求',
      '【内容创作执行 按计划】根据写作任务计划，自动生成文章：选题→大纲→撰写→优化',
      '【内容审核提交 创作后】完成文章后提交审核，确保内容质量和合规性',
      '【排名数据监控 每日】监控排名追踪数据，对排名下滑的关键词发起内容刷新任务',
      '【爆文复刻执行 每周】执行爆文复刻任务：分析全网高流量内容→提取成功要素→生成同类内容',
      '【AI深度审计 每篇】执行AI深度审计：EEAT评分→实体优化→结构化数据→内链优化',
      '【自媒体发布管理 持续】管理自媒体账号发布计划，按内容日历自动发布',
    ],
    collaborationDesc: [
      '→ 祝清远（内容运营总监）：直接上级，每日汇报内容创作进度；内容策略调整需祝清远审批',
      '→ 卓砚秋（自媒体运营专员）：GEO内容创作完成后，协调卓砚秋进行自媒体分发',
      '→ 鲁晚汀（知识审核专员）：内容涉及知识库更新时，协调鲁晚汀审核',
      '→ 郗明砚（安全合规卫士）：内容合规审查配合郗明砚执行',
      '→ 柏语嫣（消息接待专员）：私域运营需要内容时，为柏语嫣提供内容支持',
    ],
    recentAction: '完成本周GEO内容创作，发布文章23篇，平均EEAT评分87分',
    lastRun: '2026-04-03 10:00:00',
    nextScheduled: undefined,
    tasksToday: 26,
    weeklyTasks: 168,
    successRate: 91,
    relatedMenus: ['geo-writing', 'geo-material', 'geo-ai-content'],
  },
  {
    id: 'SYS-N03',
    deptId: 'dept-ops-media',
    code: 'sys_media_social',
    name: '卓砚秋',
    gender: 'male',
    title: '自媒体运营专员',
    avatar: 'https://readdy.ai/api/search-image?query=Chinese%20male%20social%20media%20operations%20specialist%20age%2027%2C%20energetic%20youthful%20face%2C%20trendy%20short%20black%20hair%20with%20slight%20quiff%2C%20trend-aware%20lively%20eyes%2C%20wearing%20vibrant%20orange%20casual%20jacket%2C%20social%20media%20savvy%20expression%2C%20bright%20orange%20gradient%20background%2C%20professional%20portrait%20photography%2C%20high%20detail%20facial%20features&width=400&height=600&seq=sysagent-n03-v2&orientation=portrait',
    status: 'running',
    level: 6,
    approvalAuthority: 'L1',
    reportsTo: 'SYS-N01',
    personality: '创意活跃、善于把握热点、执行力强、数据敏感',
    background: '专注于各自媒体平台的内容运营，负责内容发布、账号管理和数据分析',
    coreAbilities: ['内容发布', '账号管理', '热点追踪', '数据分析', '互动运营', '平台策略'],
    responsibilities: [
      '管理抖音、视频号、小红书、B站、快手等平台的账号运营',
      '执行内容发布计划，确保各平台内容按时发布',
      '追踪热点话题，及时创作和发布热点相关内容',
      '分析各平台数据，优化内容策略和发布时间',
      '管理账号互动，提升粉丝活跃度和参与度',
      '监控账号健康状态，防止账号风险',
    ],
    kpis: ['内容发布准时率 ≥98%', '粉丝增长率 ≥3%/月', '互动率提升 ≥5%/月'],
    techStack: {
      llm: [{ model: 'Deepseek-V3', purpose: '热点话题分析、互动内容生成、平台数据分析报告' }],
      knowledgeBases: [
        { name: '自媒体运营策略库', purpose: '各平台运营规则、发布策略、互动技巧' },
        { name: '热点话题库', purpose: '各平台热点话题数据，用于内容创作参考' },
        { name: '账号管理规范库', purpose: '账号健康维护规则、违规处理方案、申诉流程' },
      ],
      workflows: [
        { name: '每日内容发布工作流', trigger: '按内容日历定时触发' },
        { name: '热点话题监控工作流', trigger: '每日触发' },
        { name: '账号健康监控工作流', trigger: '每日触发' },
        { name: '每周数据分析工作流', trigger: '每周触发' },
      ],
      skills: [
        { name: '自媒体发布API', type: 'API', purpose: '向各平台账号发布内容，管理发布计划' },
        { name: '热点话题爬虫', type: 'RPA', purpose: '抓取各平台热点话题，识别流量机会' },
        { name: '平台数据读取API', type: 'API', purpose: '读取各平台内容数据、粉丝增长、互动数据' },
        { name: '账号状态监控工具', type: 'API', purpose: '监控账号健康状态，发现违规风险' },
      ],
      memory: { type: '短期记忆', desc: '记录当前发布任务的执行状态；历史运营数据存储在自媒体管理系统' },
    },
    workflowDesc: [
      '【多平台账号管理 每日】管理抖音、视频号、小红书、B站、快手等平台账号：状态监控→内容审核→违规处理',
      '【内容发布执行 按计划】执行内容发布计划，确保各平台内容按时发布：定时发布→标签优化→互动引导',
      '【热点话题追踪 每日】追踪热点话题，及时创作和发布热点相关内容，把握流量机会',
      '【平台数据分析 每周】分析各平台数据：播放量→完播率→互动率→粉丝增长，优化内容策略',
      '【账号互动管理 每日】管理账号互动，回复评论，提升粉丝活跃度和参与度',
      '【账号健康监控 持续】监控账号健康状态，防止账号风险，及时处理异常情况',
    ],
    collaborationDesc: [
      '→ 祝清远（内容运营总监）：直接上级，每日汇报自媒体数据；运营策略调整需祝清远审批',
      '→ 蒋晚汀（GEO内容专员）：接收蒋晚汀创作的内容，进行自媒体平台适配后发布',
      '→ 甄晚霞（视频生产专员）：视频内容发布与甄晚霞协作',
      '→ 卞清越（矩阵分发专员）：自媒体数据与卞清越共享，联合优化分发策略',
      '→ 郗明砚（安全合规卫士）：内容合规审查配合郗明砚执行',
    ],
    recentAction: '完成本周各平台内容发布，小红书新增粉丝1.2k，抖音视频平均播放量提升18%',
    lastRun: '2026-04-03 08:00:00',
    nextScheduled: '2026-04-03 20:00:00',
    tasksToday: 32,
    weeklyTasks: 204,
    successRate: 90,
    relatedMenus: ['geo-selfmedia', 'geo-media', 'engine-social'],
  },
];

// ─── 辅助函数 ──────────────────────────────────────────────────────────────────

/** 根据部门ID获取该部门的所有员工 */
export function getAgentsByDept(deptId: string): SystemAgentProfile[] {
  return systemAgents.filter(a => a.deptId === deptId);
}

/** 根据员工ID获取员工信息 */
export function getAgentById(id: string): SystemAgentProfile | undefined {
  return systemAgents.find(a => a.id === id);
}

/** 获取某员工的直接下属 */
export function getSubordinates(agentId: string): SystemAgentProfile[] {
  return systemAgents.filter(a => a.reportsTo === agentId);
}

/** 获取运行中的员工数量 */
export function getRunningCount(): number {
  return systemAgents.filter(a => a.status === 'running' || a.status === 'executing').length;
}

/** 获取今日总任务数 */
export function getTotalTasksToday(): number {
  return systemAgents.reduce((sum, a) => sum + a.tasksToday, 0);
}

/** 获取平均成功率 */
export function getAvgSuccessRate(): number {
  return Math.round(systemAgents.reduce((sum, a) => sum + a.successRate, 0) / systemAgents.length);
}
